import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button } from '../../Components/UI/button';
import { Input } from '../../Components/UI/input';
import { Label } from '../../Components/UI/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose, } from '../../Components/UI/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, } from '../../Components/UI/select';
import { MultiSelect } from '../../Components/UI/multi-select';
import { Textarea } from '../../Components/UI/textarea';
import { Checkbox } from '../../Components/UI/checkbox';
import { Plus, X, ArrowLeft, Check, Settings, Phone, MessageCircle, Handshake, MapPin, PenLine, MoreHorizontal, Triangle, Minus, ChevronsDown, ChevronsUp, Calendar, RotateCcw, Mic, MicOff, Bell, Trash2, SquareCheckBig, CalendarClock, GitFork, ShieldCheck, ChevronDown } from 'lucide-react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { getAgencyDetails, getThreadTypesArr, getUsersArr, getAllUsersArr, isSmartSellerMode, userIsAdmin, getUserConfig } from '../../Store/Reducers';
import { format, parse, set } from 'date-fns';
import { TimeInputWithDialog } from '../../Components/Common/TimePickerWatch';
import ConnectionEntryDialog from '../Connections/ConnectionEntry';
import { SpeechControls } from '../../Components/Common/SpeechControls';
import { SHOW_QUICK_TASK_AI } from '../../Utils/FeatureFlags';
import UserEntry from '../Users/UserEntry';
import CustomSearchableDropdown from '../../Components/Common/CustomSearchableDD';
import { DatePicker } from '../../Components/Common/DatePicker';
import MastersDialog from '../Settings/Masters';
import { languages as languagesCatalog, defaultVoiceLanguages, states as statesCatalog } from '../../Components/Enums/DataEnums';
import { capitalizeFirstLetter, combineDateTime, currentUserHasPermission, getAgencyNameByID, getConnectionOptionsWithSearch, getMobileHeaderTitle, getRelativeDateText, getThreadModeTitle, getThreadTypeMeta, getTaskCategoryIDForUser, getMyAgenciesMappedIDs, isIOSDevice, openInExternalBrowser, extractObserverUserIDs, calculateSuggestedReminder, getBeforeDueReminder } from '../../Utils/Utilities';
import { manageTaskAPI } from './TasksAPI';
import apiService from '../../Services/APIService';
import { priorities } from '../../Utils/Constants';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../Components/UI/tooltip';
import TaskCategoryManager from '../../Components/Common/TaskCategoryManager';
import { getConnectionByID, getFromIndexedDB } from '../../Utils/IndexedDBUtils';
import { getCookie, setCookie } from '../../Utils/CookieHandler';
import useIsMobile from '../../Hooks/useIsMobile';
import { triggerHaptic } from '../../Hooks/useHaptics';
import { getAuthData } from '../../Utils/AuthUtils';
import { checkIfExists } from '../../Services/LoginAPI';
import { getContractsArr } from '../../Store/Reducers';
import { getActiveThreadTypesArr } from '../../Store/Reducers';
import { is } from 'date-fns/locale';
import Policies from '../Settings/Policies/Policies';
import { BsWhatsapp } from 'react-icons/bs';
import taskClassConfig from '../../Config/taskClassConfig.json';

const getClassIcon = (iconName, className = "h-4 w-4 shrink-0") => {
    switch (iconName) {
        case 'SquareCheckBig':
            return <SquareCheckBig className={className} />;
        case 'Calendar':
            return <Calendar className={className} />;
        case 'CalendarClock':
            return <CalendarClock className={className} />;
        case 'GitFork':
            return <GitFork className={className} />;
        case 'ShieldCheck':
            return <ShieldCheck className={className} />;
        default:
            return null;
    }
};

const TaskEntry = forwardRef(
    (
        {
            dialogTitle = "Task Entry",
            triggerButton = true,
            triggerText = "Add Task",
            open: externalOpen,
            onOpenChange: externalOnOpenChange,
            prefilledData = null,
            onTaskAdded,
            extFieldUse = false,
            initialFocusField = null,
        },
        ref
    ) => {
        // Get tomorrow's date in dd-MMM-yy format
        const tomorrowDT = new Date();
        tomorrowDT.setDate(tomorrowDT.getDate() + 1);

        const isNewTask = !prefilledData?.taskID || Number(prefilledData?.taskID) === 0;

        const uConfig = getUserConfig();
        let userEODTime = '17:00';
        let userPlanTime = '10:00';
        if (uConfig?.TaskConfig) {
            try {
                const parsed = JSON.parse(uConfig.TaskConfig);
                if (parsed && parsed[0]) {
                    userEODTime = parsed[0].EOD || '17:00';
                    userPlanTime = parsed[0].PLN || '10:00';
                }
            } catch (err) {
                console.error('Failed to parse TaskConfig:', err);
            }
        }

        const getRoundedReminder = (candidateDate, nowTime, dueDateTime) => {
            if (!candidateDate) return null;
            const ms = 1000 * 60;
            let roundedMs = Math.ceil(candidateDate.getTime() / (5 * ms)) * (5 * ms);
            let roundedDate = new Date(roundedMs);
            if (roundedDate > dueDateTime) {
                return dueDateTime;
            }
            return roundedDate;
        };

        const getSuggestedTodayTime = (nowTime = new Date()) => {
            const candidate = new Date(nowTime);
            const curMin = candidate.getMinutes();
            const rem = curMin % 15;
            candidate.setMinutes(rem === 0 ? curMin + 15 : curMin + (15 - rem), 0, 0);
            if ((candidate.getTime() - nowTime.getTime()) < 15 * 60 * 1000) {
                candidate.setMinutes(candidate.getMinutes() + 15, 0, 0);
            }
            return format(candidate, 'HH:mm');
        };

        let defaultDT;
        let defaultTime;

        if (isNewTask) {
            const now = new Date();
            const [eodH, eodM] = userEODTime.split(':').map(Number);
            const eodToday = new Date(now);
            eodToday.setHours(eodH, eodM, 0, 0);

            if (now < eodToday) {
                defaultDT = format(now, 'dd-MMM-yy');
                defaultTime = userEODTime;
            } else {
                defaultDT = format(tomorrowDT, 'dd-MMM-yy');
                defaultTime = userEODTime;
            }
        } else {
            defaultDT = format(tomorrowDT, 'dd-MMM-yy');
            defaultTime = userEODTime;
        }
        const isMobile = useIsMobile();
        const canAddConnections = currentUserHasPermission('ConnEntry');
        // Get current logged in user/agency
        const curLoggedInUser = JSON.parse(localStorage.getItem('Jarvis') ?? '[]')?.[0]?.UserID ?? '';
        // Default taskAgencyID to the currently active agency instead of '0' (agency-independent) -
        // '0' should only ever be reached deliberately (e.g. an explicit agency-independent selection).
        const curLoggedInAgencyID = JSON.parse(localStorage.getItem('Jarvis') ?? '[]')?.[0]?.AgencyID ?? 0;

        // Initial object of task data fields
        const initialFormData = {
            connID: `${prefilledData?.connID ? prefilledData?.connID : ''}`,
            taskName: '',
            description: `${prefilledData?.description ? prefilledData?.description : ''}`,
            dueDT: prefilledData?.dueDT ? format(new Date(prefilledData?.dueDT), 'dd-MMM-yy') : defaultDT,
            dueTime: prefilledData?.dueTime ? prefilledData.dueTime : defaultTime,
            pocUserID: curLoggedInUser,
            priority: 'M',
            threads: prefilledData?.threadIDs ? prefilledData?.threadIDs : '',
            status: 1,
            taskType: capitalizeFirstLetter(prefilledData?.taskType) || 'Internal',
            contracts: prefilledData?.ctrcIDs ? prefilledData?.ctrcIDs : [],
            secConnIDs: prefilledData?.secConnIDs || [],
            isRemind: getCookie('DefaultTaskRemind') === 'true',
            remindBefore: '',
            remindDate: '',
            remindTime: '',
            remindType: 'before',
            myCategory: 0,
            taskAgencyID: prefilledData?.AgencyID !== undefined ? String(prefilledData.AgencyID) : String(curLoggedInAgencyID),
            canComplete: prefilledData?.canComplete != undefined ? prefilledData.canComplete : true,
            repeatAfter: prefilledData?.repeatAfter || 0,
            repeatDT: prefilledData?.repeatDT ? format(new Date(prefilledData.repeatDT), 'dd-MMM-yy hh:mm a') : '',
            taskClass: prefilledData?.class || prefilledData?.Class || prefilledData?.taskClass || 1,
            options: prefilledData?.options || (Number(prefilledData?.class || prefilledData?.Class || prefilledData?.taskClass || 1) === 3 ? ['', ''] : null),
            observerUserIDs: extractObserverUserIDs(prefilledData),
        }

        // Local state variables
        const [internalOpen, setInternalOpen] = useState(false);
        const [showReminderTag, setShowReminderTag] = useState(false); // New state to control Tag view vs Input view
        const [connectionDialogOpen, setConnectionDialogOpen] = useState(false);
        const [userEntryDialogOpen, setUserEntryDialogOpen] = useState(false);
        const [isReminderFieldsDisabled, setIsReminderFieldsDisabled] = useState(false)
        const [connectionSearchText, setConnectionSearchText] = useState('');
        const [taskTypeEntryOpen, setTaskTypeEntryOpen] = useState(false);
        const [showClassPopover, setShowClassPopover] = useState(false);
        const [allConnections, setAllConnections] = useState([]);

        const loadAllConns = async () => {
            const conns = await getFromIndexedDB('Conns') || [];
            setAllConnections(conns);
        };

        useEffect(() => {
            loadAllConns();
        }, []);

        const [taskFormData, setTaskFormData] = useState(initialFormData);
        const [repeatEnabled, setRepeatEnabled] = useState(Boolean(prefilledData?.repeatAfter) || Boolean(prefilledData?.repeatDT));
        const isLockedClass = Boolean(prefilledData?.taskID) && ((Number(prefilledData?.class || prefilledData?.Class || prefilledData?.taskClass) === 3 || Number(prefilledData?.class || prefilledData?.Class || prefilledData?.taskClass) === 4) || taskFormData.taskClass === 3 || taskFormData.taskClass === 4);
        const [repeatDayDraft, setRepeatDayDraft] = useState(null);
        const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
        const setIsOpen = externalOnOpenChange || setInternalOpen;
        const [openDescription, setOpenDescription] = useState(false);
        const [isDescriptionManuallyOpened, setIsDescriptionManuallyOpened] = useState(false);
        const [openContact, setOpenContact] = useState(false);
        const [isContactManuallyOpened, setIsContactManuallyOpened] = useState(false);
        const [openTaskTime, setOpenTaskTime] = useState(true);
        const [isTaskTimeModified, setIsTaskTimeModified] = useState(false);
        const [isThreadSelectDisabled, setIsThreadSelectDisable] = useState(true);
        const [threadSelectPlaceholder, setThreadSelectPlaceholder] = useState("Select Threads");
        const [filteredThreads, setFilteredThreads] = useState([]);
        const isUserAdmin = useSelector(userIsAdmin);
        const isNextTaskMode = Boolean(
            extFieldUse ||
            prefilledData?.isNextTask ||
            prefilledData?.taskLabel === 'Next Task' ||
            dialogTitle === 'Next Task Entry'
        );
        const [isExtFieldUse, setIsExtFieldUse] = useState(extFieldUse ? extFieldUse : isNextTaskMode);
        const [contracts, setContracts] = useState(prefilledData?.ctrcIDs || []);
        const [isInitialLoad, setIsInitialLoad] = useState(true);
        const prevPocUserIDRef = useRef(undefined);
        const lastMonthDayRef = useRef(null);
        const lastRepeatAfterRef = useRef(null);
        const [shareByEmail, setShareByEmail] = useState(false);
        const [shareByWhatsApp, setShareByWhatsApp] = useState(false);
        const [emailConfigOpen, setEmailConfigOpen] = useState(false);
        const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
        const [categoryManagerInitialView, setCategoryManagerInitialView] = useState('list');
        const [myCategories, setMyCategories] = useState(() => {
            try { return JSON.parse(getUserConfig()?.TaskCtgs || '[]'); } catch { return []; }
        });
        const [zoomLevel, setZoomLevel] = useState(1);

        const agencyData = useSelector(getAgencyDetails);

        const [smartAddText, setSmartAddText] = useState('');
        const [smartAddLoading, setSmartAddLoading] = useState(false);

        const handleChromeAISmartAdd = async (overrideText = null) => {
            const textToUse = typeof overrideText === 'string' ? overrideText : smartAddText;
            if (!textToUse.trim()) return;
            setSmartAddLoading(true);
            try {
                let apiKey = localStorage.getItem('GEMINI_API_KEY');
                apiKey = process.env.AI_API_KEY
                if (!apiKey) {
                    throw new Error("Gemini API key is not configured. Please set it in the browser console: localStorage.setItem('GEMINI_API_KEY', 'your_key')");
                }

                const todayRef = new Date().toISOString().split('T')[0];
                const categoriesJSON = JSON.stringify(myCategories.map(c => ({ CID: c.CID, CNm: c.CNm })));
                const contactsJSON = JSON.stringify(allConnections.slice(0, 55).map(c => ({ ConnID: c.ConnID, Name: c.bizName || c.contactName || '' })));

                const prompt = `
                You are a CRM command parser. Today's date is: ${todayRef}.
                Available Categories: ${categoriesJSON}
                Available Contacts: ${contactsJSON}

                Task command: "${textToUse}"

                Please parse the command and extract the structured values:
                1. For "categoryID": Match the task command's words against the Available Categories list. Look for direct mentions (e.g., "personal", "sales", "merchandise") or semantic phrases (e.g., "mark this as personal", "merchandise task", "sales discussion"). Map it to the matching category's CID. Return null if no category matches.
                2. For "connID": Identify any person/business name mentioned in the command (e.g., "meet Harsh", "call Meet", "follow up with Suthar") and find the closest match in the Available Contacts list. Return the corresponding ConnID. Return null if no contact matches.
                3. For "taskName": Clean the task description by removing category keywords, date/time phrases, and "category" qualifiers. E.g., "Follow up with Harsh tomorrow category Sales" -> "Follow up with Harsh".
                4. For "dueDT" and "dueTime": Determine dates based on today's reference.

                Respond ONLY with a clean JSON object containing:
                {
                  "taskName": "extracted short task name description",
                  "dueDT": "YYYY-MM-DD format based on relative/absolute date in command",
                  "dueTime": "HH:MM 24h format if time mentioned, otherwise leave empty",
                  "categoryID": matched category CID (integer or null),
                  "connID": matched contact ConnID (integer or null)
                }

                Respond ONLY with the JSON block. Do not include markdown code block syntax.
                `;

                const models = ['gemini-2.5-flash', 'gemini-pro'];
                let response;
                let success = false;
                let lastError = null;

                for (const modelName of models) {
                    try {
                        const url = `https://generativelanguage.googleapis.com/v1beta/models/${modelName}:generateContent?key=${apiKey}`;
                        response = await fetch(url, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                contents: [{ parts: [{ text: prompt }] }],
                                generationConfig: {
                                    responseMimeType: 'application/json'
                                }
                            })
                        });
                        if (response.ok) {
                            success = true;
                            break;
                        } else {
                            const errData = await response.json().catch(() => ({}));
                            lastError = new Error(errData?.error?.message || `API error for ${modelName}`);
                        }
                    } catch (e) {
                        lastError = e;
                    }
                }

                if (!success) {
                    throw lastError || new Error("All Gemini model attempts failed.");
                }

                const resData = await response.json();
                const responseText = resData.candidates?.[0]?.content?.parts?.[0]?.text || '';
                const jsonText = responseText.replace(/```json/gi, '').replace(/```/g, '').trim();
                const parsed = JSON.parse(jsonText);

                setTaskFormData(prev => {
                    const updated = { ...prev };
                    if (parsed.taskName) updated.taskName = parsed.taskName;
                    if (parsed.dueDT) {
                        try {
                            updated.dueDT = format(new Date(parsed.dueDT), 'dd-MMM-yy');
                        } catch {
                            updated.dueDT = parsed.dueDT;
                        }
                    }
                    if (parsed.dueTime) updated.dueTime = parsed.dueTime;
                    if (parsed.categoryID) {
                        updated.myCategory = Number(parsed.categoryID);
                    }
                    if (parsed.connID) {
                        updated.connID = String(parsed.connID);
                    }
                    return updated;
                });
                toast.success("Task details pre-filled successfully using Gemini!");
                setSmartAddText('');
            } catch (err) {
                console.error("Gemini parsing failed:", err);
                toast.error(err.message || "Failed to parse command using Gemini API.");
            } finally {
                setSmartAddLoading(false);
            }
        };

        const [userAgencies, setUserAgencies] = useState(() => {
            try {
                const raw = localStorage.getItem('MyAgencies');
                return raw ? JSON.parse(raw) : [];
            } catch {
                return [];
            }
        });
        const [selectedAgencyID, setSelectedAgencyID] = useState(String(curLoggedInAgencyID));
        const isVoiceInputAllowed = true;// [78, 10712, 52, 40900].includes(Number(selectedAgencyID));
        const authData = getAuthData();
        const currentUserID = Number(authData?.UserID);

        const allTasksForUsage = useSelector(state => state.taskData?.data) || [];
        const categoryUsageCounts = useMemo(() => {
            const counts = {};
            allTasksForUsage.forEach(t => {
                const cid = getTaskCategoryIDForUser(t, currentUserID);
                if (cid) counts[cid] = (counts[cid] || 0) + 1;
            });
            return counts;
        }, [allTasksForUsage, currentUserID]);

        useEffect(() => {
            if (isOpen && authData?.Mobile) {
                const fetchAgencies = async () => {
                    try {
                        const result = await checkIfExists(authData.Mobile, authData.CC);
                        if (result?.Table1) {
                            setUserAgencies(result.Table1);
                        }
                    } catch (error) {
                        console.error("Failed to fetch profiles for task entry:", error);
                    }
                };
                fetchAgencies();
            }
        }, [isOpen, authData?.Mobile, authData?.CC]);

        // useModalBackButton(isOpen, setIsOpen)

        useEffect(() => {
            if (!isOpen) return;

            const timer = window.setTimeout(() => {
                let target = null;

                if (initialFocusField) {
                    target = document.getElementById(initialFocusField) || document.querySelector(`[data-field-id="${initialFocusField}"]`);
                }

                if (!target) {
                    // No specific target â€” focus the first visible, enabled input or textarea in the dialog
                    target = document.querySelector('[role="dialog"] input:not([disabled]):not([type="hidden"]), [role="dialog"] textarea:not([disabled])');
                }

                target?.focus?.({ preventScroll: false });
            }, 250);

            return () => window.clearTimeout(timer);
        }, [isOpen, initialFocusField]);

        useEffect(() => {
            const handleZoomChange = () => {
                const zoom = window.devicePixelRatio || 1;
                setZoomLevel(zoom);
            };

            window.addEventListener('resize', handleZoomChange);

            return () => {
                window.removeEventListener('resize', handleZoomChange);
            };
        }, []);

        const getDialogMaxWidth = () => {
            if (isSmartSellerMode()) {
                return !isMobile ? `${Math.min(70, 42 * zoomLevel)}vw` : undefined;
            } else if (hasConnectionSelected) {
                return !isMobile ? `${Math.min(70, 53 * zoomLevel)}vw` : undefined;
            } else {
                return !isMobile ? `${Math.min(70, 35 * zoomLevel)}vw` : undefined;
            }
        };

        // Data which is comming from store (redux)
        const users = useSelector(getAllUsersArr);
        const contractsFromRedux = useSelector(getContractsArr);
        const { data: connectionData } = useSelector(state => state.connectionData);
        const { data: threadData } = useSelector(state => state.threadData);
        const recordTypes = useSelector((state) => state.masterData.recordTypes || []);
        const showModule = { contracts: agencyData?.EnblContracts };
        const [secondaryContacts, setSecondaryContacts] = useState([]);
        const allowSecConns = useSelector(state => state.masterData.allowSecConns);
        const isOAuthEnabled = useSelector((state) => state.masterData?.isOAuthEnabled || false);
        const allowLinkedThreads = false;
        const allowLinkedCtrcs = false;

        const taskTypesData = useSelector((state) => state.masterData.recordTypes);

        // Whether a user (other than the logged-in user themself) can be assigned/see the given connection,
        // based on that user's own ConnViewTypeID access rules.
        const canUserAccessConnection = (user, connection) => {
            if (!user || !connection) return false;
            const userConnViewTypeID = user.connViewTypeID ?? 1;
            const connType = connection.ConnType || '';
            if (userConnViewTypeID === 1) return true;
            if (userConnViewTypeID === 2) return connType.includes('C') || connType.includes('P');
            if (userConnViewTypeID === 3) return connType.includes('S');
            if (userConnViewTypeID === 4) {
                const uid = String(user.userID);
                const pocUserIDs = (connection.POCUserIDs || '').split(',').map(id => id.trim());
                const thrPocIDs = (connection.ThrPOCUserIDs || '').split(',').map(id => id.trim());
                return String(connection.POCUserID) === uid || pocUserIDs.includes(uid) || thrPocIDs.includes(uid);
            }
            return true;
        };

        // The same mobile number can have one Users row per agency (multi-agency). All such rows
        // represent "me" - collapse them into a single "Me" entry and resolve the correct
        // per-agency UserID based on whichever agency is currently in context.
        const selfUserRows = useMemo(() => {
            if (!authData?.Mobile) return [];
            return users.filter(u => String(u.mobile) === String(authData.Mobile) && String(u.cc) === String(authData.CC));
        }, [users, authData?.Mobile, authData?.CC]);

        const isSelfUserID = (id) => {
            const myUserIDs = getMyAgenciesMappedIDs('UserID').map(Number);
            return selfUserRows.some(u => String(u.userID) === String(id)) ||
                (Boolean(curLoggedInUser) && String(id) === String(curLoggedInUser)) ||
                myUserIDs.includes(Number(id));
        };

        // Once a connection is selected, only show executives from that connection's agency
        // who actually have access to it. Connection selection takes priority (matches taskAgencyID logic below).
        const agencyFilteredUsers = useMemo(() => {
            if (taskFormData.connID && String(taskFormData.connID) !== '0') {
                const conn = allConnections.find(c => String(c.ConnID) === String(taskFormData.connID));
                if (conn) {
                    const connAgencyID = Number(conn.AgencyID || 0);
                    return users
                        .filter(u => Number(u.agencyID || 0) === connAgencyID && canUserAccessConnection(u, conn))
                        .map(u => isSelfUserID(u.userID) ? { ...u, name: 'Myself' } : u);
                }
            }
            if (selfUserRows.length > 1) {
                const currentAgencyID = Number(agencyData?.AgencyID || 0);
                const meRow = selfUserRows.find(u => String(u.userID) === String(taskFormData.pocUserID))
                    || selfUserRows.find(u => Number(u.agencyID) === currentAgencyID)
                    || selfUserRows[0];
                const otherUsers = users.filter(u => !isSelfUserID(u.userID));
                return [{ ...meRow, name: 'Myself' }, ...otherUsers];
            }
            return users.map(u => isSelfUserID(u.userID) ? { ...u, name: 'Myself' } : u);
        }, [taskFormData.connID, taskFormData.pocUserID, allConnections, users, selfUserRows, agencyData?.AgencyID]);

        const [showCoHostField, setShowCoHostField] = useState(() => {
            const isMeeting = Number(prefilledData?.class || prefilledData?.Class || prefilledData?.taskClass || 1) === 2;
            if (isMeeting) {
                const prefilledPoc = prefilledData?.pocUserID || curLoggedInUser;
                const isSelf = selfUserRows.some(u => String(u.userID) === String(prefilledPoc)) || (Boolean(curLoggedInUser) && String(prefilledPoc) === String(curLoggedInUser));
                return !isSelf;
            }
            return true;
        });

        useEffect(() => {
            if (Number(taskFormData.taskClass) === 2) {
                setShowCoHostField(!isSelfUserID(taskFormData.pocUserID));
            } else {
                setShowCoHostField(true);
            }
        }, [taskFormData.taskClass]);

        // Once an executive (not yourself) is selected, only show connections from their agency that they have access to.
        // Only applies when no connection is already selected - connection selection takes priority.
        const agencyFilteredConnections = useMemo(() => {
            const hasConnSelected = taskFormData.connID && String(taskFormData.connID) !== '0';
            const hasOtherUserSelected = taskFormData.pocUserID
                && String(taskFormData.pocUserID) !== '0'
                && !isSelfUserID(taskFormData.pocUserID);

            if (!hasConnSelected && hasOtherUserSelected) {
                const user = users.find(u => String(u.userID) === String(taskFormData.pocUserID));
                if (user) {
                    const userAgencyID = Number(user.agencyID || 0);
                    return allConnections.filter(c => Number(c.AgencyID || 0) === userAgencyID && canUserAccessConnection(user, c));
                }
            }
            return allConnections;
        }, [taskFormData.connID, taskFormData.pocUserID, allConnections, users, curLoggedInUser]);

        const uniqueUserAgencies = useMemo(() => {
            const seen = new Set();
            return userAgencies.filter(agency => {
                const id = agency.AgencyID ? String(agency.AgencyID) : "";
                if (!id || seen.has(id)) return false;
                seen.add(id);
                return true;
            });
        }, [userAgencies]);

        const agencyAndSelectedFilteredConnections = useMemo(() => {
            let connections = agencyFilteredConnections;
            if (selectedAgencyID && selectedAgencyID !== 'all') {
                connections = connections.filter(conn => String(conn.AgencyID) === String(selectedAgencyID));
            }
            return connections;
        }, [agencyFilteredConnections, selectedAgencyID]);


        const taskTypes = useMemo(() => {
            return taskTypesData.map((taskType) => {
                return {
                    label: taskType.Typ, value: taskType.Typ
                }
            })
        }, [taskTypesData]);

        const ALLOWED_TASK_TYPES = {
            Call: 'Phone',
            Message: 'MessageCircle',
            Meeting: 'Handshake',
            Visit: 'MapPin'
        };

        const threadTypesData = useSelector(getThreadTypesArr);
        const [currentConnection, setCurrentConnection] = useState({});
        const activeThreadTypesArr = useSelector(getActiveThreadTypesArr);
        const currActiveThreadType = getThreadModeTitle(activeThreadTypesArr);
        const [showDropdown, setShowDropdown] = useState(false);
        const [autoOpenDropdown, setAutoOpenDropdown] = useState(false);

        useEffect(() => {
            const isAllowed = Object.keys(ALLOWED_TASK_TYPES).includes(taskFormData.taskType);
            // Automatically show dropdown if the selected type is not an icon type
            if (!isAllowed && taskFormData.taskType && !currentConnection) {
                setShowDropdown(true);
            } else if (!isAllowed && prefilledData?.taskID) {
                setShowDropdown(true);
            }
        }, [taskFormData.taskType, prefilledData?.taskID]);


        let taskType = ''
        if (prefilledData?.taskType) {
            const matchingType = taskTypesData.find(
                rt => rt.Typ.toLowerCase() === prefilledData.taskType.toLowerCase()
            );
            taskType = matchingType ? matchingType.Typ : capitalizeFirstLetter(prefilledData.taskType);
        }

        const msPerDay = 24 * 60 * 60 * 1000;
        const normalizeDate = (d) => new Date(new Date(d).setHours(0, 0, 0, 0));
        const isWithinSevenDays = (normalizeDate(taskFormData.dueDT) - normalizeDate(Date.now())) / msPerDay <= 7;

        useEffect(() => {
            if (!isOpen) {
                setIsInitialLoad(true);
                prevPocUserIDRef.current = undefined;
                lastMonthDayRef.current = null;
                lastRepeatAfterRef.current = null;
                return;
            }

            // If we are currently adding a task type, do not reset form data
            if (taskTypeEntryOpen) {
                return;
            }

            if (prefilledData && isInitialLoad) {
                const updatedFormData = {
                    connID: prefilledData?.connID || '',
                    taskName: prefilledData?.taskName || '',
                    description: prefilledData?.description || '',
                    dueDT: prefilledData?.dueDT ? format(new Date(prefilledData?.dueDT), 'dd-MMM-yy') : defaultDT,
                    dueTime: prefilledData?.dueTime || defaultTime,
                    pocUserID: prefilledData?.pocUserID || curLoggedInUser,
                    priority: prefilledData?.priority || 'M',
                    threads: prefilledData?.threads || '',
                    status: 1,
                    taskType: taskType || (taskTypes.length > 0 ? taskTypes[0].Typ : ''),
                    contracts: prefilledData?.ctrcIDs || [],
                    secConnIDs: prefilledData?.secConnIDs || [],
                    isRemind: getCookie('DefaultTaskRemind') === 'true',
                    remindBefore: '',
                    remindDate: '',
                    remindTime: '',
                    remindType: 'before',
                    myCategory: prefilledData?.myCategory || 0,
                    taskAgencyID: prefilledData?.AgencyID !== undefined ? String(prefilledData.AgencyID) : String(curLoggedInAgencyID),
                    canComplete: prefilledData?.canComplete != undefined ? prefilledData.canComplete : true,
                    repeatAfter: prefilledData?.repeatAfter || 0,
                    repeatDT: prefilledData?.repeatDT ? format(new Date(prefilledData.repeatDT), 'dd-MMM-yy hh:mm a') : '',
                    taskClass: prefilledData?.class || prefilledData?.Class || prefilledData?.taskClass || 1,
                    options: prefilledData?.options || (Number(prefilledData?.class || prefilledData?.Class || prefilledData?.taskClass || 1) === 3 ? ['', ''] : null),
                    observerUserIDs: extractObserverUserIDs(prefilledData),
                }

                setRepeatEnabled(Boolean(prefilledData?.repeatAfter) || Boolean(prefilledData?.repeatDT))


                // Handle existing reminder (rmdrDT) logic
                const now = new Date();
                const isTaskDueInPast = updatedFormData.dueDT && updatedFormData.dueTime
                    ? new Date(combineDateTime(updatedFormData.dueDT, updatedFormData.dueTime)) < now
                    : false;

                if (prefilledData?.rmdrDT) {
                    const rmdrDateObj = new Date(prefilledData.rmdrDT.replace(" ", "T")); // Safely parse yyyy-MM-dd HH:mm:ss
                    if (!isNaN(rmdrDateObj.getTime())) {
                        const isReminderInPast = rmdrDateObj < now;
                        if (isReminderInPast) {
                            updatedFormData.isRemind = false;
                            updatedFormData.remindDate = '';
                            updatedFormData.remindTime = '';
                            setShowReminderTag(false);
                            setIsReminderFieldsDisabled(false);
                        } else {
                            updatedFormData.isRemind = true;
                            updatedFormData.remindDate = format(rmdrDateObj, 'dd-MMM-yy');
                            updatedFormData.remindTime = format(rmdrDateObj, 'HH:mm');
                            setShowReminderTag(false);
                            setIsReminderFieldsDisabled(isTaskDueInPast);
                        }
                    }
                } else {
                    // No existing reminder
                    if (isTaskDueInPast) {
                        // For overdue tasks without reminder, checkbox should be unchecked
                        updatedFormData.isRemind = false;
                        setIsReminderFieldsDisabled(false);
                        setShowReminderTag(false);
                    } else if (updatedFormData.isRemind && updatedFormData.dueDT && updatedFormData.dueTime) {
                        // When isRemind defaults to true via cookie, auto-calculate suggested reminder date/time
                        const dueDateTime = new Date(combineDateTime(updatedFormData.dueDT, updatedFormData.dueTime));
                        if (!isNaN(dueDateTime.getTime())) {
                            const suggested = calculateSuggestedReminder(dueDateTime, userPlanTime, now);
                            if (suggested?.date && suggested?.time) {
                                updatedFormData.remindDate = format(suggested.date, 'dd-MMM-yy');
                                updatedFormData.remindTime = suggested.time;
                            }
                        }
                    }
                }

                const isMeeting = Number(updatedFormData.taskClass || 1) === 2;
                setShowCoHostField(isMeeting ? !isSelfUserID(updatedFormData.pocUserID || curLoggedInUser) : true);

                setTaskFormData(updatedFormData);
                setSelectedAgencyID(prefilledData?.AgencyID !== undefined ? String(prefilledData.AgencyID) : String(curLoggedInAgencyID));
                if (updatedFormData.description) {
                    setOpenDescription(true);
                    // Don't set isDescriptionManuallyOpened here - description is auto-opened from prefilled data
                }
                if (updatedFormData.connID && updatedFormData.connID !== '0') {
                    setOpenContact(true);
                }

                setIsInitialLoad(false);

            } else if (isInitialLoad) {
                const freshInitialFormData = {
                    ...initialFormData,
                    dueDT: defaultDT,
                    dueTime: defaultTime
                };
                setTaskFormData(freshInitialFormData);
                setSelectedAgencyID(String(curLoggedInAgencyID));
                const isMeeting = Number(freshInitialFormData.taskClass || 1) === 2;
                setShowCoHostField(isMeeting ? !isSelfUserID(freshInitialFormData.pocUserID || curLoggedInUser) : true);
                setIsInitialLoad(false);
            }
        }, [prefilledData, isOpen, defaultDT, defaultTime, curLoggedInUser, taskTypeEntryOpen]);

        // Helper to determine taskAgencyID based on selected connection and assigned user
        useEffect(() => {
            if (isInitialLoad) return;

            const determineTaskAgencyID = () => {
                // If a connection is selected, it takes priority and links the task to that connection's agency
                if (taskFormData.connID && String(taskFormData.connID) !== '0') {
                    const connObj = allConnections.find(c => String(c.ConnID) === String(taskFormData.connID));
                    if (connObj) {
                        return String(connObj.AgencyID || curLoggedInAgencyID);
                    }
                }

                // If no connection is selected, check assigned executive (pocUserID)
                if (taskFormData.pocUserID && String(taskFormData.pocUserID) !== '0') {
                    if (isSelfUserID(taskFormData.pocUserID)) {
                        const selfRow = selfUserRows.find(u => String(u.userID) === String(taskFormData.pocUserID));
                        return String(selfRow?.agencyID || curLoggedInAgencyID);
                    }
                    const userObj = users.find(u => String(u.userID) === String(taskFormData.pocUserID));
                    if (userObj) {
                        return String(userObj.agencyID || curLoggedInAgencyID);
                    }
                }
                return String(curLoggedInAgencyID);
            };

            const computedAgencyID = determineTaskAgencyID();
            if (computedAgencyID !== taskFormData.taskAgencyID) {
                setTaskFormData(prev => ({
                    ...prev,
                    taskAgencyID: computedAgencyID
                }));
            }
        }, [taskFormData.connID, taskFormData.pocUserID, allConnections, users, isInitialLoad, curLoggedInUser, selfUserRows]);
        useEffect(() => {
            if (isInitialLoad) return;
            if (selfUserRows.length <= 1) return;
            if (!isSelfUserID(taskFormData.pocUserID)) return;
            if (!(taskFormData.connID && String(taskFormData.connID) !== '0')) return;

            const conn = allConnections.find(c => String(c.ConnID) === String(taskFormData.connID));
            if (!conn) return;
            const targetAgencyID = Number(conn.AgencyID || 0);

            const matchingSelf = selfUserRows.find(u => Number(u.agencyID) === targetAgencyID);
            if (matchingSelf && String(matchingSelf.userID) !== String(taskFormData.pocUserID)) {
                setTaskFormData(prev => ({
                    ...prev,
                    pocUserID: matchingSelf.userID
                }));
            }
        }, [taskFormData.connID, allConnections, selfUserRows, isInitialLoad]);

        // If you are both the creator and the assignee (self-assigned task), canComplete is always true -
        // there's no "other" executive to restrict, and the checkbox is hidden in that case anyway.
        useEffect(() => {
            if (isInitialLoad) return;
            if (isSelfUserID(taskFormData.pocUserID) && taskFormData.canComplete != true) {
                setTaskFormData(prev => ({ ...prev, canComplete: true }));
            }
        }, [taskFormData.pocUserID, selfUserRows, isInitialLoad]);

        // For meeting tasks: filter selected observers to only keep users of the POC's agency when POC changes
        useEffect(() => {
            if (isInitialLoad) return;
            if (prevPocUserIDRef.current === undefined) {
                prevPocUserIDRef.current = taskFormData.pocUserID;
                return;
            }
            if (prevPocUserIDRef.current !== taskFormData.pocUserID) {
                prevPocUserIDRef.current = taskFormData.pocUserID;

                if (taskFormData.pocUserID && String(taskFormData.pocUserID) !== '0' && !isSelfUserID(taskFormData.pocUserID)) {
                    const assigneeUser = users.find(u => String(u.userID) === String(taskFormData.pocUserID));
                    if (assigneeUser?.agencyID && uniqueUserAgencies.some(a => String(a.AgencyID) === String(assigneeUser.agencyID))) {
                        setSelectedAgencyID(String(assigneeUser.agencyID));
                    }
                }

                if (Number(taskFormData.taskClass) === 2) {
                    const newPocUserID = taskFormData.pocUserID;
                    let targetAgencyID = 0;

                    if (newPocUserID && String(newPocUserID) !== '0' && !isSelfUserID(newPocUserID)) {
                        const pocUser = users.find(u => String(u.userID) === String(newPocUserID));
                        if (pocUser) {
                            targetAgencyID = Number(pocUser.agencyID || 0);
                        }
                    } else {
                        targetAgencyID = Number(agencyData?.AgencyID || 0);
                    }

                    if (targetAgencyID) {
                        setTaskFormData(prev => {
                            const currentObservers = Array.isArray(prev.observerUserIDs) ? prev.observerUserIDs : [];
                            const filteredObservers = currentObservers.filter(id => {
                                const user = users.find(u => String(u.userID) === String(id));
                                if (!user) return false;
                                return Number(user.agencyID || 0) === targetAgencyID;
                            });
                            return {
                                ...prev,
                                observerUserIDs: filteredObservers
                            };
                        });
                    } else {
                        setTaskFormData(prev => ({
                            ...prev,
                            observerUserIDs: []
                        }));
                    }
                }
            }
        }, [taskFormData.pocUserID, isInitialLoad, taskFormData.taskClass, users, agencyData?.AgencyID]);

        // Handle thread filtering when prefilledData changes
        useEffect(() => {
            if ((!prefilledData?.connID && !taskFormData?.connID) || !isOpen) {
                setFilteredThreads([]);
                setIsThreadSelectDisable(true);
                return;
            }
            const fetchData = async () => {
                let selectedConnID = prefilledData?.connID || taskFormData.connID;
                if (selectedConnID && connectionData.length > 0 && threadData.length > 0) {
                    let matchedThreads = [];
                    let contracts = [];

                    const connection = await getConnectionByID(selectedConnID);
                    setCurrentConnection(connection);

                    const openThreadIDsArr = connection?.OpenThreadIDs?.split(",") || [];
                    const contractIDs = connection?.CtrcIDs?.split(",") || [];

                    threadData.sort((a, b) => {
                        return new Date(a.SinceDT) - new Date(b.SinceDT);
                    });

                    threadData.forEach((thread) => {
                        if (openThreadIDsArr.includes(String(thread.ThreadID))) {
                            const sinceDT = thread.SinceDT ? getRelativeDateText(new Date(thread.SinceDT), false, false) : '';
                            const targetDT = thread.DueDT ? getRelativeDateText(new Date(thread.DueDT), false, false) : '';
                            let selThreadTypeInfo = getThreadTypeMeta(thread.TypeID, threadTypesData);
                            let formattedThread = {
                                value: String(thread.ThreadID),
                                threadType: selThreadTypeInfo.nameSng,
                                product: thread.MainProduct,
                                label: `${selThreadTypeInfo.nameSng} - ${thread.MainProduct}(${sinceDT})`,
                                icon: selThreadTypeInfo.Icon,
                                color: selThreadTypeInfo.colorClass,
                                typeID: thread.TypeID,
                                sinceDT: sinceDT,
                                targetDT: targetDT,
                                threadID: thread.ThreadID,
                                secConnIDs: thread.SecConnIDs
                            };
                            matchedThreads.push(formattedThread);
                        }
                    });

                    if (matchedThreads.length === 0) {
                        setIsThreadSelectDisable(true);
                        setThreadSelectPlaceholder("No threads found for this contact.");
                    } else {
                        setIsThreadSelectDisable(false);
                        setThreadSelectPlaceholder("Select Threads");
                    }
                    setFilteredThreads(matchedThreads);

                    contracts = contractsFromRedux.filter(contract => contract.ContractID && contractIDs.includes(String(contract.ContractID)))
                    setContracts(contracts);

                } else if (prefilledData?.connID) {
                    setIsThreadSelectDisable(true);
                    setThreadSelectPlaceholder("No threads found for this contact.");
                    setFilteredThreads([]);
                    setTaskFormData((prev) => ({
                        ...prev,
                        threads: ''
                    }))
                }
            };

            fetchData();
        }, [threadData, threadTypesData, taskFormData.connID, prefilledData?.connID, connectionData.length, isOpen, contractsFromRedux]);

        // Set default reminder value when isRemind is checked
        useEffect(() => {
            // CRITICAL FIX: If we are displaying the existing reminder Tag, 
            // DO NOT auto-calculate or shift dates based on the "1 hour before" logic.
            // The existing reminder is a fixed point in time and should not move relative to the Due Date.
            if (showReminderTag) return;

            if (taskFormData.isRemind && !taskFormData.remindBefore) {
                const dueDateTime = new Date(combineDateTime(taskFormData.dueDT, taskFormData.dueTime));
                const now = new Date();
                const minutesUntilDue = Math.floor((dueDateTime - now) / 60000);

                // Set default based on time available
                let defaultReminder = '15';
                if (minutesUntilDue >= 15) {
                    defaultReminder = '15';
                } else if (minutesUntilDue >= 5) {
                    defaultReminder = '5';
                } else if (minutesUntilDue > 0) {
                    defaultReminder = '0';
                }

                handleFieldChange("remindBefore", defaultReminder);
            }
        }, [taskFormData.isRemind, taskFormData.dueDT, taskFormData.dueTime, showReminderTag]);

        useEffect(() => {
            if ((taskFormData.threads.length != 1) || !isOpen) {
                // setTaskFormData((prev) => ({
                //     ...prev,
                //     secConnIDs: []
                // }))
                setSecondaryContacts([])
                return;
            };
            if (!filteredThreads.length) return;
            const thrForSecConns = filteredThreads.find(thr => taskFormData.threads[0] == thr.threadID);
            const secConnIDs = thrForSecConns?.secConnIDs.split(",").map(id => Number(id)).filter(Boolean);
            // Secondary connections can belong to a different agency than the one currently active,
            // so resolve them against the full, unfiltered connection set (allConnections) rather than
            // the agency-scoped redux connectionData.
            const secContacts = allConnections
                .filter(conn => secConnIDs?.includes(conn.ConnID) && (conn.IsCold !== 1 && conn.IsCold !== true))
                .map((c) => {
                    return {
                        value: c.ConnID,
                        label: !c.BizName || c.BizName.trim() === '-' ? c.ContactName : c.BizName
                    }
                }).sort((a, b) => a.label.localeCompare(b.label))

            setSecondaryContacts(secContacts)
        }, [taskFormData?.threads.length, isOpen, filteredThreads.length, allConnections])

        // Get task data by using reference
        useImperativeHandle(ref, () => ({
            getExtTaskData: () => {
                // Convert empty arrays to empty strings for API
                let rmdrDT = '';

                if (taskFormData.isRemind && taskFormData.remindDate && taskFormData.remindTime) {
                    rmdrDT = combineDateTime(taskFormData.remindDate, taskFormData.remindTime);
                }

                const safeJoin = (val) => {
                    if (Array.isArray(val)) return val.join(',');
                    if (typeof val === 'string') return val;
                    return '';
                };

                const dataToSend = {
                    ...taskFormData,
                    threads: safeJoin(taskFormData.threads),
                    contracts: safeJoin(taskFormData.contracts),
                    secConnIDs: safeJoin(taskFormData.secConnIDs),
                    rmdrDT: rmdrDT
                };
                return { validation: validateTaskFormData(taskFormData), data: dataToSend };
            }
        }))

        // useEffect(() => {
        // 		setShowDropdown(false);
        // 	}, []);

        const hasConnectionSelected = Boolean(taskFormData.connID);
        // Handle every fields
        const handleFieldChange = (fieldName, value) => {
            if (fieldName == 'isRemind') {
                setCookie('DefaultTaskRemind', String(Boolean(value)));
            }
            // Handling for date and time fields validations
            if (fieldName === "dueDT" || fieldName === "dueTime") {
                // Get the date and time values (use new value for the field being changed)
                const dateToCheck = fieldName === "dueDT" ? value : taskFormData.dueDT;
                const timeToCheck = fieldName === "dueTime" ? value : taskFormData.dueTime;
                const resultDT = new Date(combineDateTime(dateToCheck, timeToCheck))

                // Validate the combination
                // if (resultDT < new Date()) {
                //     const errorMessage = "Invalid Date-Time";
                //     const description =
                //         fieldName === "dueTime"
                //             ? "Please select a present or past time. Future time is not allowed."
                //             : "Please select a present or past date and time. Future date/time is not allowed.";
                //     toast.error(errorMessage, { description: description });

                //     if (fieldName === "dueTime") {
                //         // Only reset time if time validation failed
                //         setTaskFormData((prev) => ({ ...prev, dueTime: prefilledData?.dueTime ? prefilledData.dueTime : defaultTime }));
                //     } else {
                //         // Reset both date and time if date validation failed
                //         setTaskFormData((prev) => ({ ...prev, dueDT: prefilledData?.dueDT ? format(new Date(prefilledData?.dueDT), 'dd-MMM-yy') : defaultDT, dueTime: prefilledData?.dueTime ? prefilledData.dueTime : defaultTime }));
                //     }
                //     return;
                // }

                // FIX: Only auto-shift the reminder time if we are NOT showing the existing reminder Tag.
                // If showReminderTag is true, we keep the reminder time as is (unless it becomes invalid, handled below).
                if (taskFormData.isRemind && !showReminderTag) {
                    const dueDateTime = new Date(combineDateTime(dateToCheck, timeToCheck));
                    const remindDateTime = getBeforeDueReminder(dueDateTime, new Date());

                    if (remindDateTime) {
                        const remindTime = format(remindDateTime, 'HH:mm');
                        const remindDate = format(remindDateTime, 'dd-MMM-yy');

                        setTaskFormData(prev => ({
                            ...prev,
                            [fieldName]: value,
                            remindTime: remindTime,
                            remindDate: remindDate
                        }));
                        return;
                    }
                }

                // VALIDATION: If reminder exists, check if new DueDT makes it invalid
                if (taskFormData.isRemind && (taskFormData.remindDate || taskFormData.remindTime)) {
                    const currentRemindStr = combineDateTime(taskFormData.remindDate, taskFormData.remindTime || '00:00');
                    const currentRemindDT = new Date(currentRemindStr);

                    // If Reminder is same or after the new Due Date, remove it
                    if (currentRemindDT >= resultDT) {
                        setTaskFormData(prev => ({
                            ...prev,
                            [fieldName]: value,
                            isRemind: false,
                            remindDate: '',
                            remindTime: ''
                        }));
                        setShowReminderTag(false); // Reset tag view
                        toast.info("Reminder removed", { description: "Due time was changed to before the reminder time." });
                        return;
                    }
                }
            }
            setTaskFormData(prev => ({
                ...prev,
                [fieldName]: value
            }));
        };

        // Re-evaluate if reminder fields should be disabled when dueDT or dueTime changes
        useEffect(() => {
            const now = new Date();
            const dueDateTime = taskFormData.dueDT && taskFormData.dueTime
                ? new Date(combineDateTime(taskFormData.dueDT, taskFormData.dueTime))
                : null;

            if (taskFormData.isRemind && taskFormData.remindDate && taskFormData.remindTime) {
                const remindDateTime = new Date(combineDateTime(taskFormData.remindDate, taskFormData.remindTime));

                // If task due date is moved to future, enable reminder fields
                if (dueDateTime && dueDateTime > now) {
                    setIsReminderFieldsDisabled(false);
                    setShowReminderTag(false); // Switch from tag to input mode
                }
                // If reminder is in the past, keep disabled
                else if (remindDateTime < now) {
                    setIsReminderFieldsDisabled(true);
                } else {
                    setIsReminderFieldsDisabled(false);
                }
            }
        }, [taskFormData.dueDT, taskFormData.dueTime]);

        useEffect(() => {
            if (!taskFormData.repeatDT) return;

            const parseCustomDate = (dateStr) => {
                if (!dateStr) return null;
                const parts = dateStr.split('-');
                if (parts.length === 3) {
                    const d = parseInt(parts[0], 10);
                    const monthStr = parts[1].toLowerCase();
                    let y = parseInt(parts[2], 10);
                    if (y < 100) y += 2000;
                    const monthsMap = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
                    const m = monthsMap[monthStr];
                    if (m !== undefined && !isNaN(d) && !isNaN(y)) return new Date(y, m, d);
                }
                return new Date(dateStr);
            };

            let repeatDTObj = parse(String(taskFormData.repeatDT), 'dd-MMM-yy hh:mm a', new Date());
            if (isNaN(repeatDTObj.getTime())) repeatDTObj = new Date(taskFormData.repeatDT);
            if (isNaN(repeatDTObj.getTime())) return;
            const currentDay = repeatDTObj.getDate();

            const dueDT = parseCustomDate(taskFormData.dueDT);
            const anchor = (dueDT && !isNaN(dueDT.getTime())) ? dueDT : new Date();
            let nextYY = anchor.getFullYear();
            let nextMM = anchor.getMonth() + 1;
            if (nextMM > 11) {
                nextMM = 0;
                nextYY += 1;
            }
            const nextMonthLastDay = new Date(nextYY, nextMM + 1, 0).getDate();
            const validDay = Math.min(currentDay, nextMonthLastDay);
            const datePart = format(new Date(nextYY, nextMM, validDay), 'dd-MMM-yy');
            const newRepeatDT = combineDateTime(datePart, taskFormData.dueTime) || datePart;

            if (newRepeatDT !== taskFormData.repeatDT) {
                setTaskFormData(prev => ({ ...prev, repeatDT: newRepeatDT }));
            }
        }, [taskFormData.dueDT, taskFormData.dueTime]);

        useEffect(() => {
            if (taskFormData.isRemind && taskFormData.dueDT && taskFormData.dueTime && !isReminderFieldsDisabled && !showReminderTag) {
                const now = new Date();
                const dueDateTime = new Date(combineDateTime(taskFormData.dueDT, taskFormData.dueTime));

                if (isNaN(dueDateTime.getTime())) return;

                const suggested = calculateSuggestedReminder(dueDateTime, userPlanTime, now);
                const defaultDate = suggested?.date ? format(suggested.date, 'dd-MMM-yy') : null;
                const defaultTime = suggested?.time || null;

                if (defaultTime && defaultDate) {
                    setTaskFormData(prev => {
                        if (prev.remindTime === defaultTime && prev.remindDate === defaultDate) {
                            return prev;
                        }
                        return {
                            ...prev,
                            remindTime: defaultTime,
                            remindDate: defaultDate
                        };
                    });
                }
            }
        }, [taskFormData.isRemind, taskFormData.dueDT, taskFormData.dueTime, isReminderFieldsDisabled, showReminderTag, userPlanTime]);

        // Handle every search select fields
        const handleSearchSelectChange = (fieldName, option, value, searchText) => {
            if (value === 'add') {
                if (fieldName === 'connID') {
                    setConnectionSearchText(searchText || '');
                    // Close current dialog and open connection dialog
                    // setIsOpen(false);
                    setConnectionDialogOpen(true);
                } else if (fieldName === 'taskType') {
                    // Keep parent dialog open when adding new task type
                    setTaskTypeEntryOpen(true);
                } else if (fieldName === 'pocUserID') {
                    setUserEntryDialogOpen(true);
                }
            } else {
                setTaskFormData(prev => ({
                    ...prev,
                    [fieldName]: option?.value
                }));

                if (fieldName === 'connID' && option?.value) {
                    if (selectedAgencyID !== 'all') {
                        const connObj = allConnections.find(c => String(c.ConnID) === String(option.value));
                        if (connObj && connObj.AgencyID) {
                            setSelectedAgencyID(String(connObj.AgencyID));
                        }
                    }
                }
            }
        }

        const handleTaskTypeAdded = (newTaskTypeName) => {
            if (newTaskTypeName) {
                setTaskFormData(prev => ({
                    ...prev,
                    taskType: newTaskTypeName
                }));
            }
            // Close the Masters dialog
            setTaskTypeEntryOpen(false);
        }

        // Handle email share checkbox change
        const handleEmailShareChange = (checked) => {
            setShareByEmail(checked);
        };

        // Handle settings icon click to open Gmail configuration
        const handleSettingsClick = () => {
            setEmailConfigOpen(true);
        };

        // Handle dialog close - reset share by email
        const handleDialogOpenChange = (open) => {
            setIsOpen(open);
            if (!open) {
                setShareByEmail(false);
                setShareByWhatsApp(false);
                setEmailConfigOpen(false);
                setIsDescriptionManuallyOpened(false);
                setIsContactManuallyOpened(false);
                // Reset description visibility only in add mode (not edit mode)
                if (!prefilledData?.taskID) {
                    setOpenDescription(false);
                    setOpenContact(false);
                    setSelectedAgencyID(String(curLoggedInAgencyID));
                }
            }
        };

        // Validate task form data
        const validateTaskFormData = (data) => {
            if (!data.taskName) {
                return {
                    valid: false,
                    error: {
                        title: "Please enter the task.",
                        description: "Task is required."
                    }
                };
            } else if (!data.pocUserID) {
                return {
                    valid: false,
                    error: {
                        title: "Please select a valid executive.",
                        description: "Executive is required."
                    }
                };
            } else if ((Number(data.class || data.Class || data.taskClass) === 4) && String(data.pocUserID) === String(curLoggedInUser)) {
                return {
                    valid: false,
                    error: {
                        title: "Distinct approver required.",
                        description: "You cannot assign an approval task to yourself."
                    }
                };
            }
            // else if (!data.taskType) {
            //     return {
            //         valid: false,
            //         error: {
            //             title: "Please select a task type.",
            //             description: "Task type is required."
            //         }
            //     };
            // }

            // Repeat Task validation
            if (repeatEnabled && !(Number(data.repeatAfter) > 0) && !data.repeatDT) {
                return {
                    valid: false,
                    error: {
                        title: "Invalid repeat interval",
                        description: "Please enter a valid repeat value."
                    }
                };
            } else if (Number(data.repeatAfter) > 0 && (Number(data.repeatAfter) < 1 || Number(data.repeatAfter) > 366)) {
                return {
                    valid: false,
                    error: {
                        title: "Invalid repeat interval",
                        description: "Please enter a number of days between 1 and 366."
                    }
                };
            } else if (data.repeatDT && isNaN(new Date(data.repeatDT).getTime())) {
                return {
                    valid: false,
                    error: {
                        title: "Invalid repeat day",
                        description: "Please select a valid day of the month."
                    }
                };
            }

            // Date and time validation
            if (data.dueDT && data.dueTime) {
                const dueDateTime = new Date(combineDateTime(data.dueDT, data.dueTime));

                if (dueDateTime < new Date()) {
                    return {
                        valid: false,
                        error: {
                            title: "Invalid Date-Time",
                            description: "Please select a present or future date and time. Past date/time is not allowed."
                        }
                    };
                }
            }

            // Reminder validation
            if (data.isRemind && data.remindDate && data.remindTime) {
                const remindDateTime = new Date(combineDateTime(data.remindDate, data.remindTime));
                const dueDateTime = new Date(combineDateTime(data.dueDT, data.dueTime));

                if (remindDateTime > dueDateTime) {
                    return {
                        valid: false,
                        error: {
                            title: "Invalid Reminder Time",
                            description: "Reminder time must be on or before the due date/time."
                        }
                    };
                }

                if (remindDateTime < new Date()) {
                    return {
                        valid: false,
                        error: {
                            title: "Invalid Reminder Time",
                            description: "Reminder time cannot be in the past."
                        }
                    };
                }
            }

            return { valid: true };
        };

        // Handle save task 
        const handleSaveTask = async () => {
            const validation = validateTaskFormData(taskFormData);


            if (!validation.valid) {
                toast.error(validation.error.title, {
                    description: validation.error.description,
                    duration: 4000,
                });
                return;
            }
            const { dueTime, dueDT, isRemind, remindBefore, ...rest } = taskFormData;

            let scheduleDT = "";
            let isRmdrUpdate = 1; // Default to 1 (Update/Add)

            if (showReminderTag) {
                // Case: Existing reminder shown as Tag. User did not touch it.
                isRmdrUpdate = 0; // Flag 0: Do not reschedule in C#

                // Pass existing value to keep DB consistent
                if (prefilledData?.rmdrDT) {
                    // Ensure format matches what API expects
                    const oldRmdr = new Date(prefilledData.rmdrDT.replace(" ", "T"));
                    if (!isNaN(oldRmdr.getTime())) {
                        scheduleDT = format(oldRmdr, 'dd-MMM-yy hh:mm aa');
                    } else {
                        scheduleDT = prefilledData.rmdrDT;
                    }
                }
            } else {
                // Case: Input Mode (User is Adding new, Editing existing, or Removing)
                isRmdrUpdate = 1; // Flag 1: Schedule in C#

                if (isRemind) {
                    setCookie('DefaultTaskRemind', 'true');
                    if (taskFormData.remindDate && taskFormData.remindTime) {
                        const reminderDate = new Date(combineDateTime(taskFormData.remindDate, taskFormData.remindTime));
                        scheduleDT = format(reminderDate, 'dd-MMM-yy hh:mm aa');
                    } else if (remindBefore && !isNaN(parseInt(remindBefore))) {
                        const dueDateTime = new Date(combineDateTime(dueDT, dueTime));
                        const reminderDate = new Date(dueDateTime.getTime() - (parseInt(remindBefore) * 60000));
                        scheduleDT = format(reminderDate, 'dd-MMM-yy hh:mm aa');
                    }
                } else {
                    setCookie('DefaultTaskRemind', 'false');
                    const isForOtherUser = taskFormData.pocUserID && !isSelfUserID(taskFormData.pocUserID);
                    if (isForOtherUser && !prefilledData?.taskID) {
                        const now = new Date();
                        const dueDateTime = new Date(combineDateTime(dueDT, dueTime));
                        if (!isNaN(dueDateTime.getTime()) && dueDateTime > now) {
                            const oneHourBefore = new Date(dueDateTime.getTime() - 60 * 60 * 1000);
                            if (oneHourBefore > now && oneHourBefore < dueDateTime) {
                                scheduleDT = format(oneHourBefore, 'dd-MMM-yy hh:mm aa');
                            } else {
                                const beforeDueCandidate = getBeforeDueReminder(dueDateTime, now);
                                if (beforeDueCandidate && beforeDueCandidate > now && beforeDueCandidate < dueDateTime) {
                                    scheduleDT = format(beforeDueCandidate, 'dd-MMM-yy hh:mm aa');
                                } else {
                                    scheduleDT = format(dueDateTime, 'dd-MMM-yy hh:mm aa');
                                }
                            }
                        } else {
                            scheduleDT = "";
                        }
                    } else {
                        // Reminder removed
                        scheduleDT = "";
                    }
                }
            }

            let email = '';
            if (shareByEmail && taskFormData.pocUserID) {
                const assignedUser = users.find(user => user.userID === taskFormData.pocUserID);
                email = assignedUser?.email || '';
            }

            const safeJoin = (val) => {
                if (Array.isArray(val)) return val.join(',');
                if (typeof val === 'string') return val;
                return '';
            };

            const threadsLen = Array.isArray(taskFormData.threads)
                ? taskFormData.threads.length
                : (taskFormData.threads ? String(taskFormData.threads).split(',').length : 0);

            const apiData = {
                ...rest,
                description: taskFormData.description,
                threads: safeJoin(taskFormData.threads),
                contracts: safeJoin(taskFormData.contracts),
                dueDT: combineDateTime(taskFormData.dueDT, taskFormData.dueTime),
                scheduleDT: scheduleDT,
                isRmdrUpdate: isRmdrUpdate, // Pass flag to service
                taskID: prefilledData?.taskID || 0,
                secConnIDs: threadsLen === 1 ? (safeJoin(taskFormData.secConnIDs) || '') : '',
                shareByEmail: shareByEmail,
                email: email,
                agencyName: agencyData?.AgencyName || '',
                taskType: taskFormData.taskType || 'Internal',
                AgencyID: prefilledData?.taskID && prefilledData?.AgencyID ? Number(prefilledData.AgencyID) : Number(agencyData?.AgencyID || 0),
                createdUserID: prefilledData?.createdUserID || 0,
                taskAgencyID: prefilledData?.taskID && prefilledData?.AgencyID ? Number(prefilledData.AgencyID) : (taskFormData.taskAgencyID && taskFormData.taskAgencyID !== '0' ? Number(taskFormData.taskAgencyID) : Number(agencyData?.AgencyID || 0)),
                class: taskFormData.taskClass || 1,
                options: Number(taskFormData.taskClass) === 4
                    ? JSON.stringify(["Approve", "Disapprove", "Other"])
                    : (taskFormData.options ? (() => {
                        let parsed = [];
                        try {
                            parsed = typeof taskFormData.options === 'string' ? JSON.parse(taskFormData.options) : taskFormData.options;
                        } catch (e) {
                            parsed = String(taskFormData.options).split(',').map(s => s.trim());
                        }
                        if (!Array.isArray(parsed)) parsed = [];
                        return JSON.stringify(parsed.map(o => typeof o === 'object' && o !== null ? (o.OTxt || '') : String(o)).filter(o => o && o.trim() !== ''));
                    })() : null),
                observerUserIDs: safeJoin(taskFormData.observerUserIDs),
            };

            // API call for ADD or UPDATE task
            // Set agency context so apiService resolves the correct UserID for the
            // task's agency (e.g. when assigned to a cross-agency executive).
            let taskAgencyIDNum = taskFormData.taskAgencyID && taskFormData.taskAgencyID !== '0'
                ? Number(taskFormData.taskAgencyID)
                : Number(agencyData?.AgencyID || 0);
            if (prefilledData?.taskID && prefilledData?.AgencyID) {
                taskAgencyIDNum = Number(prefilledData.AgencyID);
            }
            const willSwitchContext = taskAgencyIDNum && taskAgencyIDNum !== Number(agencyData?.AgencyID || 0);

            if (willSwitchContext) {
                apiService.setAgencyContext(taskAgencyIDNum);
            }
            try {
                let result;
                if (prefilledData?.taskID) {
                    // Update existing task
                    result = await manageTaskAPI(apiData, 'EDIT');
                    // toast.success(`Task updated!`, { duration: 3000 });
                } else {
                    // Add new task
                    result = await manageTaskAPI(apiData, 'ADD');
                    // toast.success(`Task saved!`, { duration: 3000 });
                }
                triggerHaptic("success");

                if (shareByWhatsApp) {
                    let taskID = prefilledData?.taskID || 0;
                    if (!taskID && result?.updatePackage) {
                        try {
                            const updateData = JSON.parse(result.updatePackage);
                            const tasksTable = Object.values(updateData).find(table =>
                                Array.isArray(table) && table[0]?.Tbl === "Tasks"
                            );
                            if (tasksTable && tasksTable.length > 0) {
                                taskID = tasksTable[0].TaskID || tasksTable[0].tskID || tasksTable[0].taskID || 0;
                            }
                        } catch (e) {
                            console.error("Error parsing task creation response for WhatsApp link:", e);
                        }
                    }

                    if (taskID) {
                        const assignedUser = users.find(user => String(user.userID) === String(taskFormData.pocUserID));
                        const phone = assignedUser?.mobile || assignedUser?.Mobile || '';
                        const cc = assignedUser?.cc || assignedUser?.CC || '91';

                        const taskClassNum = Number(taskFormData.taskClass || 1);
                        let completeByText = "";
                        if (taskFormData.dueDT) {
                            const now = new Date();
                            const dueDateTime = new Date(combineDateTime(taskFormData.dueDT, taskFormData.dueTime || '09:00'));

                            const hasZeroMinutes = dueDateTime.getMinutes() === 0;
                            const timeStr = hasZeroMinutes
                                ? format(dueDateTime, 'h a')
                                : format(dueDateTime, 'h:mm a');

                            const todayMidnight = new Date(now);
                            todayMidnight.setHours(0, 0, 0, 0);

                            const dueMidnight = new Date(dueDateTime);
                            dueMidnight.setHours(0, 0, 0, 0);

                            const tomorrowMidnight = new Date(todayMidnight);
                            tomorrowMidnight.setDate(tomorrowMidnight.getDate() + 1);

                            let dayStr = "";
                            if (dueMidnight.getTime() === todayMidnight.getTime()) {
                                dayStr = "today";
                            } else if (dueMidnight.getTime() === tomorrowMidnight.getTime()) {
                                dayStr = "tomorrow";
                            } else {
                                dayStr = "on " + format(dueDateTime, 'd-MMM');
                            }

                            if (taskClassNum === 2) {
                                completeByText = `Please join by ${timeStr} ${dayStr}.`;
                            } else if (taskClassNum === 3) {
                                completeByText = `Please decide by ${timeStr} ${dayStr}.`;
                            } else if (taskClassNum === 4) {
                                completeByText = `Please approve by ${timeStr} ${dayStr}.`;
                            } else {
                                completeByText = `Please complete by ${timeStr} ${dayStr}.`;
                            }
                        }

                        let introText = "";
                        if (taskClassNum === 2) {
                            introText = `I have scheduled a meeting: ${taskFormData.taskName}.`;
                        } else if (taskClassNum === 3) {
                            introText = `I need your decision on: ${taskFormData.taskName}.`;
                        } else if (taskClassNum === 4) {
                            introText = `I need your approval for: ${taskFormData.taskName}.`;
                        } else {
                            introText = `I have created a task: ${taskFormData.taskName}.`;
                        }
                        const message = `${introText}${completeByText ? ' ' + completeByText : ''}`;

                        const cleanedPhone = String(phone).replace(/[^\d]/g, "");
                        const cleanedCC = String(cc).replace(/[^\d]/g, "");
                        const whatsappUrl = `https://api.whatsapp.com/send/?phone=${cleanedCC}${cleanedPhone}&text=${encodeURIComponent(message)}`;

                        window.open(whatsappUrl, '_blank');
                    }
                }

                apiService.clearAgencyContext();
                setTaskFormData(initialFormData);
                setShareByWhatsApp(false);
                setSelectedAgencyID(String(curLoggedInAgencyID));
                setOpenDescription(false);
                setIsDescriptionManuallyOpened(false);
                setOpenContact(false);
                setIsContactManuallyOpened(false);
                setIsOpen(false);

                // Call the onTaskAdded callback if provided
                if (onTaskAdded && typeof onTaskAdded === 'function') {
                    onTaskAdded();
                }
            } catch (error) {
                apiService.clearAgencyContext();
                console.error(`Error in saving the task:`, error);
                const taskClassNum = Number(taskFormData.taskClass || 1);
                const classLabel = taskClassNum === 2 ? "Meeting" : taskClassNum === 3 ? "Decision" : taskClassNum === 4 ? "Approval" : "Task";
                if (error.isBusinessLogicError) {
                    toast.error(`This ${classLabel.toLowerCase()} could not be saved.`, { description: error.message, duration: 6000 });
                } else {
                    toast.error(`This ${classLabel.toLowerCase()} could not be saved.`, { description: "Please try again or contact support if the issue persists.", duration: 4000 });
                }
            }
        };


        const renderDisableConn = () => {
            const wasOriginallyAssigned = prefilledData?.connID && prefilledData?.connID !== '0';
            const isShow = isMobile && taskFormData.connID && prefilledData?.connectionLocked && wasOriginallyAssigned && !extFieldUse;
            const selectedConnection = allConnections.find(conn => String(conn.ConnID) === String(taskFormData.connID));
            const businessName = selectedConnection?.BizName || selectedConnection?.ContactName || 'Loading...';

            return isShow ? (
                <div key='connID' className="space-y-1.5">
                    <Label htmlFor='connID'>
                        {prefilledData?.connectionLabel || 'Contact'}
                    </Label>
                    <Input
                        id='connID'
                        name='connID'
                        value={businessName}
                        onChange={() => { }} // No-op since it's disabled
                        placeholder={'Contact'}
                        className="h-11 text-sm transition-all duration-200 bg-white sahi-input"
                        type={'text'}
                        autoComplete={'off'}
                        maxLength={50}
                        disabled={true}
                    />
                </div>
            ) : null;
        };

        const renderDynamicFields = () => {
            const isSwapNeeded = !prefilledData?.connID;
            const wasOriginallyAssigned = prefilledData?.connID && prefilledData?.connID !== '0';

            const contactField = {
                id: 'connID',
                label: `${prefilledData?.connectionLabel || 'Contact'}`,
                placeholder: 'Select Contact',
                type: 'searchSelect',
                required: false,
                category: 'basic',
                allowAdd: canAddConnections ? true : false,
                addText: 'New Contact',
                priority: 1.5
            };

            const getTaskNameLabel = () => {
                const cls = Number(taskFormData.taskClass || 1);
                if (cls === 2) return 'Meeting';
                if (cls === 3) return 'Decision';
                if (cls === 4) return 'Approval';
                return prefilledData?.taskLabel || 'Task';
            };

            const getTaskNamePlaceholder = () => {
                if (Number(taskFormData.taskClass || 1) === 3) return 'e.g. Which pricing plan should we choose?';
                if (Number(taskFormData.taskClass || 1) === 4) return 'e.g. Contract review approval';
                return 'e.g. Follow-up with client';
            };

            const getDescriptionPlaceholder = () => {
                if (Number(taskFormData.taskClass || 1) === 3) return 'e.g. Describe the context or options for the decision...';
                if (Number(taskFormData.taskClass || 1) === 4) return 'e.g. Provide details for the approval request...';
                return 'e.g. Discuss contract details';
            };

            const getPocUserLabel = () => {
                const cls = Number(taskFormData.taskClass || 1);
                if (cls === 2) return 'Co-host';
                if (cls === 3) return 'Decision by';
                if (cls === 4) return 'Approver';
                return prefilledData?.pocUserLabel || (isNewTask ? 'Assign to' : 'Assigned to');
            };

            const agencyField = {
                id: 'selectedAgencyID',
                label: 'Business',
                placeholder: 'Select Agency',
                type: 'agencyDropDown',
                required: false,
                category: 'basic',
                priority: 1.8
            };

            const taskNameField = {
                id: 'taskName',
                label: getTaskNameLabel(),
                placeholder: getTaskNamePlaceholder(),
                type: 'text',
                required: true,
                category: 'basic',
                priority: 1
            };

            const descriptionField = {
                id: 'description',
                label: `${prefilledData?.descriptionLabel || 'Description'}`,
                placeholder: getDescriptionPlaceholder(),
                type: 'textarea',
                required: false,
                category: 'basic',
                priority: 1.2
            };

            const topFields = [taskNameField, descriptionField, contactField, agencyField];

            const taskFields = [
                {
                    id: 'taskClass',
                    label: 'Task Class',
                    type: 'classSelect',
                    required: true,
                    category: 'basic',
                    priority: 0.5
                },
                ...topFields,
                ...(taskFormData.taskClass === 3 ? [{
                    id: 'options',
                    label: 'Decision Options',
                    type: 'optionsEditor',
                    required: false,
                    category: 'basic',
                    priority: 2.5
                }] : []),
                // {
                //     id: 'description',
                //     label: `${prefilledData?.descriptionLabel || 'Description'}`,
                //     placeholder: 'e.g. Discuss contract details',
                //     type: 'textarea',
                //     required: false,
                //     category: 'basic',
                //     priority: 3
                // },
                {
                    id: 'dueDT',
                    label: `${prefilledData?.dateLabel || 'Date'}`,
                    placeholder: 'Select',
                    type: 'date',
                    required: true,
                    category: 'basic',
                    priority: 4
                },
                {
                    id: 'dueTime',
                    label: `${prefilledData?.timeLabel || 'Time'}`,
                    placeholder: 'Select',
                    type: 'time',
                    required: false,
                    category: 'basic',
                    priority: 5
                },
                ...(!isSmartSellerMode() ? [{
                    id: 'pocUserID',
                    label: getPocUserLabel(),
                    placeholder: 'Select Executive',
                    type: 'dropDown',
                    emptyMessage: 'No executives found',
                    required: true,
                    category: 'basic',
                    priority: 6,
                    allowAdd: isUserAdmin,
                    addText: 'Add User'
                }] : []),
                ...(Number(taskFormData.taskClass || 1) !== 1 ? [{
                    id: 'observerUserIDs',
                    label: 'Observers',
                    placeholder: 'Select Observers',
                    type: 'observersMultiSelect',
                    required: false,
                    category: 'basic',
                    priority: Number(taskFormData.taskClass) === 2 ? 5.9 : 6.5
                }] : []),
                {
                    id: 'taskType',
                    label: `${prefilledData?.typeLabel || 'Type'}`,
                    placeholder: 'Select Type',
                    type: 'dropDown',
                    emptyMessage: 'No types found',
                    required: false,
                    category: 'basic',
                    allowAdd: true,
                    addText: 'Add New Type',
                    priority: 7,
                    showIcons: true
                },
                {
                    id: 'shareByEmail',
                    label: 'Share by Email',
                    type: 'shareByEmailCheckbox',
                    required: false,
                    category: 'basic',
                    priority: 7.5
                },
                {
                    id: 'shareByWhatsApp',
                    label: 'Send by WhatsApp',
                    type: 'shareByWhatsAppCheckbox',
                    required: false,
                    category: 'basic',
                    priority: 7.55
                },
                {
                    id: 'isRemind',
                    label: 'Remind at',
                    placeholder: 'Select Duration',
                    type: 'remindAtCheckbox',
                    required: false,
                    category: 'basic',
                    priority: 7.6
                },
                ...(Number(taskFormData.taskClass) === 2 && !showCoHostField ? [{
                    id: 'addCoHostBtn',
                    label: '',
                    type: 'addCoHostButton',
                    required: false,
                    category: 'basic',
                    priority: 7.65
                }] : []),
                {
                    id: 'remindAtDateTimePicker',
                    label: 'Reminder Date & Time',
                    type: 'remindAtDateTimePicker',
                    required: false,
                    category: 'basic',
                    priority: 7.7
                },
                {
                    id: 'priority',
                    label: 'Priority',
                    placeholder: 'Select Priority',
                    type: 'priorityButtons',
                    required: false,
                    category: 'basic',
                    priority: 7.8
                },
                {
                    id: 'myCategory',
                    label: 'Growth Area',
                    placeholder: 'Select Growth Area',
                    type: 'myCategory',
                    required: false,
                    category: 'basic',
                    priority: 7.9
                },
                {
                    id: 'repeatTask',
                    label: 'Repeat',
                    type: 'repeatTaskConfig',
                    required: false,
                    category: 'basic',
                    priority: 8.0
                },
            ];
            if (allowLinkedThreads) {
                taskFields.push({
                    id: 'threads',
                    label: `${prefilledData?.threadsLabel || 'Linked ' + currActiveThreadType.typPlr}`,
                    placeholder: `Select ${currActiveThreadType.typPlrLower}`,
                    type: 'multiSelect',
                    required: false,
                    category: 'additional',
                    priority: 9
                })
            }
            if (allowLinkedCtrcs) {
                taskFields.push({
                    id: 'contracts',
                    label: `${prefilledData?.contractsLabel || 'Linked Contracts'}`,
                    placeholder: 'Select Contract',
                    type: 'multiSelect',
                    required: false,
                    category: 'additional',
                    priority: 10
                })
            }
            if (allowSecConns) {
                taskFields.push({
                    id: 'secConnIDs',
                    label: 'Secondary Contacts',
                    placeholder: 'Select Contact',
                    type: 'multiSelectSecConns',
                    required: false,
                    category: 'additional',
                    priority: 11
                });
            }


            // Function which is arranging all form fields
            const arrangeFieldsInRows = (fields) => {
                const rows = [];
                const usedIDs = new Set();
                const isMeeting = Number(taskFormData.taskClass) === 2;
                const isMeetingMobile = isMeeting && isMobile;
                const isAssignedToOther = !isSelfUserID(taskFormData.pocUserID);
                const isMobileAssignedToOther = isMobile && isAssignedToOther;

                // Show 3-column layout for all users now
                const groups = [
                    ['connID', 'selectedAgencyID'],
                    isMeetingMobile ? ['taskType', ['dueDT', 'dueTime']] : ['taskType', ['dueDT', 'dueTime'], 'pocUserID'],
                    ['observerUserIDs'],
                    ...(isMeetingMobile ? [['pocUserID']] : []),
                    ['shareByEmail', 'shareByWhatsApp', 'isRemind', 'addCoHostBtn'],
                    ['remindAtDateTimePicker'],
                    ['threads', 'contracts']
                ];

                const sortedFields = [...fields].sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99));

                sortedFields.forEach((field) => {
                    if (usedIDs.has(field.id)) return;

                    // Find group that contains this field (even inside nested arrays)
                    const group = groups.find((group) =>
                        group.flat().includes(field.id)
                    );

                    if (group) {
                        const allIDs = group.flat();
                        const isGroupUsed = allIDs.some((id) => usedIDs.has(id));
                        if (!isGroupUsed) {
                            // Keep original nesting, but include only found fields
                            const groupFields = group
                                .map((item) => {
                                    if (Array.isArray(item)) {
                                        return item
                                            .map((id) => fields.find((f) => f.id === id))
                                            .filter(Boolean);
                                    } else {
                                        return fields.find((f) => f.id === item);
                                    }
                                })
                                .filter(Boolean);

                            rows.push(groupFields);
                            allIDs.forEach((id) => usedIDs.add(id));
                            return;
                        }
                    }

                    // Not in any group or already used
                    rows.push([field]);
                    usedIDs.add(field.id);
                });
                return rows;
            };

            const filteredTaskFields = taskFields.filter(field => {
                // Priority is rendered inline with the task name field
                if (field.id === 'priority') return false;

                // Hide taskType for external use / next task mode, or until connection/contact is selected
                if (field.id === 'taskType' && (isNextTaskMode || isExtFieldUse || !hasConnectionSelected)) {
                    return false;
                }

                // Only hide if it was originally locked AND originally had a valid connection
                if (field.id === 'connID' && ((prefilledData?.connectionLocked && wasOriginallyAssigned) || isExtFieldUse)) {
                    return false;
                }
                if (field.id === 'selectedAgencyID' && ((prefilledData?.connectionLocked && wasOriginallyAssigned) || isExtFieldUse || uniqueUserAgencies.length <= 1)) {
                    return false;
                }

                // Filter out description and connID if they are closed/not yet open
                if (field.id === 'description' && !openDescription) {
                    return false;
                }
                if (field.id === 'connID' && !openContact) {
                    return false;
                }
                if (field.id === 'selectedAgencyID' && (!openContact || uniqueUserAgencies.length <= 1)) {
                    return false;
                }

                if (field.id === 'threads' && filteredThreads.length === 0) {
                    return false;
                }
                if (field.id === 'threads' && prefilledData?.threadLocked) {
                    return false;
                }
                if (field.id === 'contracts' && !showModule.contracts) {
                    return false;
                }
                if ((field.id === 'contracts' && contracts.length === 0) || (field.id === 'contracts' && prefilledData?.contractLocked)) {
                    return false;
                }
                if (field.id === 'secConnIDs' && secondaryContacts.length === 0) {
                    return false;
                }
                // Allow pocUserID for all users, not just admins
                // if (field.id === 'pocUserID' && !isUserAdmin) return false;
                if (field.id === 'pocUserID' && Number(taskFormData.taskClass) === 2 && !showCoHostField) {
                    return false;
                }
                if (field.id === 'addCoHostBtn') {
                    return Number(taskFormData.taskClass) === 2 && !showCoHostField;
                }

                // Hide unsupported next task fields when in Next Task / External mode
                if (isNextTaskMode) {
                    if (field.id === 'taskType') return false;
                    if (field.id === 'repeatTask') return false;
                    if (field.id === 'myCategory') return false;
                    if (field.id === 'observerUserIDs') return false;
                    if (field.id === 'selectedAgencyID') return false;
                    if (field.id === 'connID') return false;
                    if (field.id === 'options') return false;
                    if (field.id === 'shareByEmail' || field.id === 'shareByWhatsApp') return false;
                    if (field.id === 'addCoHostBtn') return false;
                }

                if (isExtFieldUse && field.id === 'taskType') {
                    return false;
                }

                // Hide email/whatsapp share options when embedded in RecordEntry
                // Keep reminder options visible so user can set reminder for next task in record entry
                if (isExtFieldUse && (field.id === 'shareByEmail' || field.id === 'shareByWhatsApp')) {
                    return false;
                }

                if (field.id === 'repeatTask' && Number(taskFormData.taskClass) === 4) return false;
                if (field.id === 'shareByEmail' || field.id === 'shareByWhatsApp') return !isSelfUserID(taskFormData.pocUserID);
                if (field.id === 'isRemind' || field.id === 'remindAtDateTimePicker') {
                    // Only show reminder checkbox and pickers when assigning to self
                    return isSelfUserID(taskFormData.pocUserID);
                }
                if (field.id === 'repeatTask' && Number(taskFormData.taskClass || 1) !== 1) {
                    return false;
                }
                if (field.id === 'taskClass') {
                    return false;
                }
                if (field.id === 'options' && Number(taskFormData.taskClass || 1) === 3 && (!taskFormData.options || taskFormData.options.length === 0)) {
                    return false;
                }
                if ((field.id === 'connID' || field.id === 'secConnIDs') && Number(taskFormData.taskClass || 1) !== 1) {
                    return false;
                }
                if (field.id === 'observerUserIDs' && ![1, 2].includes(Number(taskFormData.taskClass || 1))) {
                    return false;
                }
                return true;
            });
            const fieldRows = arrangeFieldsInRows(filteredTaskFields);

            const showAddDescBtn = !openDescription && taskFields.some(f => f.id === 'description');
            const showLinkContactBtn = !isNextTaskMode && !openContact && Number(taskFormData.taskClass) == 1 && taskFields.some(f => f.id === 'connID') && !((prefilledData?.connectionLocked && wasOriginallyAssigned) || isExtFieldUse);
            const showAddOptionsBtn = !isNextTaskMode && Number(taskFormData.taskClass || 1) === 3 && (!taskFormData.options || taskFormData.options.length === 0) && !isLockedClass;

            const finalRows = [];
            const insertAfterId = openDescription ? 'description' : 'taskName';
            fieldRows.forEach((row) => {
                finalRows.push(row);
                const hasTargetField = row.some(item => !Array.isArray(item) && item?.id === insertAfterId);
                if (hasTargetField && (showAddDescBtn || showLinkContactBtn || showAddOptionsBtn)) {
                    finalRows.push([{
                        id: 'buttonsRow',
                        type: 'buttonsRow',
                        showAddDescBtn,
                        showLinkContactBtn,
                        showAddOptionsBtn
                    }]);
                }
            });

            return finalRows.map((row, rowIndex) => {
                let processedRow = row;
                const hasDateTimeGroup = row.some(item => Array.isArray(item) && item.some(field => field.id === 'dueDT' || field.id === 'dueTime'));
                const hasPocUserID = row.some(item => !Array.isArray(item) && item.id === 'pocUserID');

                if (hasDateTimeGroup && hasPocUserID && row.length === 2 && !Array.isArray(row[0])) {
                    processedRow = [null, ...row];
                }

                const topLevelCount = processedRow.length;
                const gridWithPocUser = ['lg:col-span-4', 'lg:col-span-5', 'lg:col-span-3'];

                const isDateTimeCoHostRow = topLevelCount === 2 && Array.isArray(processedRow[0]) && processedRow[0].some(f => f.id === 'dueDT') && processedRow[1]?.id === 'pocUserID';

                let gridCols = 'grid-cols-1';

                const isRemindRow = topLevelCount === 1 && !Array.isArray(processedRow[0]) && processedRow[0]?.id === 'isRemind';
                const isShareByEmailRow = topLevelCount === 1 && !Array.isArray(processedRow[0]) && processedRow[0]?.id === 'shareByEmail';
                const isCheckboxPairRow = topLevelCount === 2 && !Array.isArray(processedRow[0]) && !Array.isArray(processedRow[1]) && processedRow[0]?.type === 'remindAtCheckbox' && processedRow[1]?.type === 'shareByEmailCheckbox';
                const isTypeDateRow = topLevelCount === 2 && Array.isArray(processedRow[1]);
                let isCheckboxOnlyRow = processedRow.every(item => !Array.isArray(item) && (item?.id === 'isRemind' || item?.id === 'shareByEmail' || item?.id === 'shareByWhatsApp' || item?.id === 'addCoHostBtn'));

                if (isCheckboxOnlyRow) {
                    gridCols = 'flex flex-wrap gap-x-6 gap-y-3 items-center';
                } else if (isRemindRow || isShareByEmailRow) {
                    gridCols = 'grid-cols-1';
                } else if (isCheckboxPairRow) {
                    gridCols = 'flex flex-wrap gap-3 items-start';
                } else if (topLevelCount === 2) {
                    if (isDateTimeCoHostRow) {
                        gridCols = 'grid-cols-1 lg:grid-cols-3';
                    } else {
                        // Smart Seller (Type + Date/Time): Type (1/3) + Date/Time (2/3) -> Total 3 cols
                        gridCols = (isTypeDateRow && isSmartSellerMode()) ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1 lg:grid-cols-2';
                    }
                } else if (topLevelCount >= 3) {
                    gridCols = `grid-cols-1 ${hasConnectionSelected ? 'lg:grid-cols-12' : 'lg:grid-cols-8'}`;
                }

                const getGridClass = (index) => {
                    if (isTypeDateRow && isSmartSellerMode()) {
                        // First item (Type) takes 1 col, Second item (Date/Time group) takes 2 cols
                        return index === 0 ? 'lg:col-span-1' : 'lg:col-span-2';
                    }
                    if (isDateTimeCoHostRow) {
                        return index === 0 ? 'lg:col-span-2' : 'lg:col-span-1';
                    }
                    if (topLevelCount >= 3) {
                        return gridWithPocUser[index];
                    }
                    return '';
                }

                isCheckboxOnlyRow = processedRow.every(item => !Array.isArray(item) && (item?.id === 'isRemind' || item?.id === 'shareByEmail' || item?.id === 'shareByWhatsApp' || item?.id === 'addCoHostBtn'));

                const isDateTimePickerRow = processedRow.some(item => !Array.isArray(item) && item?.id === 'remindAtDateTimePicker');

                const isReminderTagActive = isCheckboxPairRow && showReminderTag && taskFormData.isRemind && taskFormData.remindDate && taskFormData.remindTime && !isReminderFieldsDisabled;

                let hasContent = false;
                const renderedCols = processedRow.map((item, colIndex) => {
                    if (item === null) {
                        const gridClass = getGridClass(colIndex);
                        return (
                            <div
                                key={`col-${colIndex}`}
                                className={`${gridClass}`}
                            />
                        );
                    }

                    if (Array.isArray(item)) {
                        const isDateTimeGroup = item.some(field => field.id === 'dueDT' || field.id === 'dueTime');
                        const gridClass = getGridClass(colIndex);

                        const subFieldsElements = item.map((subField, subIndex) => {
                            const renderedSubField = renderFormField(subField);
                            if (renderedSubField === null) return null;
                            hasContent = true;
                            return (
                                <React.Fragment key={`sub-${subField.id || subIndex}`}>
                                    {renderedSubField}
                                </React.Fragment>
                            );
                        }).filter(Boolean);

                        if (subFieldsElements.length === 0) return null;

                        return (
                            <div
                                key={`col-${colIndex}`}
                                className={`grid ${gridClass} grid-cols-2 gap-4`}
                            >
                                {subFieldsElements}
                            </div>
                        );
                    } else {
                        // Single field
                        const gridClass = isCheckboxOnlyRow ? '' : getGridClass(colIndex);
                        const renderedField = renderFormField(item);

                        if (renderedField === null) {
                            return null;
                        }
                        hasContent = true;

                        const colClassName = isReminderTagActive
                            ? 'contents lg:block lg:space-y-1.5'
                            : `${gridClass} ${isCheckboxOnlyRow ? '' : 'gap-4'}`;

                        return (
                            <div
                                key={`col-${colIndex}`}
                                className={colClassName}
                            >
                                {renderedField}
                            </div>
                        );
                    }
                });

                if (!hasContent) {
                    return null;
                }

                const isButtonsRow = processedRow.some(item => !Array.isArray(item) && item?.id === 'buttonsRow');

                const hasAddCoHost = processedRow.some(item => !Array.isArray(item) && item?.id === 'addCoHostBtn');
                const checkboxRowClass = hasAddCoHost ? 'flex items-center justify-between w-full' : 'flex gap-3 items-center';

                return (
                    <div
                        key={`row-${rowIndex}`}
                        className={`${isCheckboxPairRow ? `flex ${isReminderTagActive ? 'flex-wrap gap-1' : 'gap-3'} items-start` : (isCheckboxOnlyRow && !isCheckboxPairRow) ? checkboxRowClass : `grid ${gridCols} gap-4`} ${isDateTimePickerRow ? '!mt-0 mb-1' : isCheckboxOnlyRow || isButtonsRow ? '!mt-[9px] mb-1' : 'mb-1'}`}
                    >
                        {renderedCols}
                    </div>
                );
            });
        };


        // Function which is rendering all fields
        const renderFormField = (field) => {
            const { id: fieldID, label, type, placeholder, required, allowAdd = false, addText = '', emptyMessage = '' } = field;

            if (fieldID === 'buttonsRow') {
                return (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        {field.showAddOptionsBtn && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground px-0 hover:text-primary"
                                onClick={() => {
                                    handleFieldChange('options', ['', '']);
                                }}
                            >
                                +<span className="underline underline-offset-4">Add Options</span>
                            </Button>
                        )}
                        {field.showAddDescBtn && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground px-0 hover:text-primary"
                                onClick={() => {
                                    setOpenDescription(true);
                                    setIsDescriptionManuallyOpened(true);
                                }}
                            >
                                +<span className="underline underline-offset-4">Add Description</span>
                            </Button>
                        )}
                        {field.showLinkContactBtn && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground px-0 hover:text-primary"
                                onClick={() => {
                                    setOpenContact(true);
                                    setIsContactManuallyOpened(true);
                                }}
                            >
                                +<span className="underline underline-offset-4">Link Contact</span>
                            </Button>
                        )}
                    </div>
                );
            }

            if (type === 'addCoHostButton') {
                return (
                    <Button
                        key={fieldID}
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground px-0 hover:text-primary h-auto font-medium"
                        onClick={() => setShowCoHostField(true)}
                    >
                        +<span className="underline underline-offset-4">Add Co-host</span>
                    </Button>
                );
            }

            const hasConnectionSelected = Boolean(taskFormData.connID);

            // Hide taskType for next task / external use, or until connection/contact is selected
            if (fieldID === 'taskType') {
                if (isNextTaskMode || isExtFieldUse) {
                    return null;
                }
                if (!hasConnectionSelected) {
                    return null;
                }
            }

            // Render all form fields that use a multiline textarea input
            if (type === 'textarea') {
                if (!openDescription) return null;
                return (
                    <div key={fieldID} className="space-y-1.5">
                        <Label htmlFor={fieldID} required={required}>
                            {label}
                        </Label>
                        <div className="relative">
                            <Textarea
                                id={fieldID}
                                name={fieldID}
                                value={taskFormData[fieldID]}
                                onChange={(e) => handleFieldChange(fieldID, e.target.value)}
                                placeholder={placeholder}
                                allowResize={false}
                                className={`min-h-20 text-sm align-top ${isVoiceInputAllowed && !isIOSDevice() ? 'pr-20' : ''}`}
                                autoFocus={isDescriptionManuallyOpened}
                                disabled={isLockedClass && fieldID === 'description'}
                            />
                            {isVoiceInputAllowed && !isIOSDevice() && (
                                <SpeechControls
                                    targetId={fieldID}
                                    value={taskFormData[fieldID]}
                                    onChange={(val) => handleFieldChange(fieldID, val)}
                                    isMobile={isMobile}
                                />
                            )}
                        </div>
                    </div>
                );
            }

            // Render all form fields that use a singleSelect input
            else if (type === 'singleSelect') {
                let options = [];

                // dynamically setting options for different fields
                if (fieldID === 'priority') {
                    options = priorities.map(priority => ({
                        label: priority.label,
                        value: priority.value
                    }));
                }

                return (
                    <div key={fieldID} className="space-y-1.5">
                        <Label htmlFor={fieldID} required={required}>
                            {label}
                        </Label>
                        <Select
                            value={taskFormData[fieldID]}
                            onValueChange={(value) => handleFieldChange(fieldID, value)}
                        >
                            <SelectTrigger className="h-11 text-sm sahi-input">
                                <span className={taskFormData[fieldID] ? "text-primary" : "text-muted-foreground"}>
                                    {options.find(opt => opt.value === taskFormData[fieldID])?.label || placeholder}
                                </span>
                            </SelectTrigger>
                            <SelectContent className="max-h-[200px] bg-primary-foreground border-gray-200 shadow-lg">
                                {options.map((option, index) => (
                                    <SelectItem key={index} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                );
            }

            else if (type === 'companyDropDown') {
                const options = [
                    { value: '0', label: 'None (Agency-Independent)' },
                    ...userAgencies.map(agency => ({
                        value: agency.AgencyID ? agency.AgencyID.toString() : "",
                        label: agency.CompanyName || 'Company'
                    }))
                ];

                return (
                    <div key={fieldID} className="space-y-1.5">
                        <Label htmlFor={fieldID} required={required}>
                            {label}
                        </Label>
                        <Select
                            value={taskFormData[fieldID] !== undefined ? String(taskFormData[fieldID]) : '-1'}
                            onValueChange={(value) => handleFieldChange(fieldID, value)}
                        >
                            <SelectTrigger className="h-11 text-sm sahi-input">
                                <span className={taskFormData[fieldID] !== undefined ? "text-primary" : "text-muted-foreground"}>
                                    {options.find(opt => opt.value === String(taskFormData[fieldID]))?.label || placeholder}
                                </span>
                            </SelectTrigger>
                            <SelectContent className="max-h-[200px] bg-primary-foreground border-gray-200 shadow-lg">
                                {options.map((option, index) => (
                                    <SelectItem key={index} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                );
            }

            else if (type === 'agencyDropDown') {
                const options = [
                    { value: 'all', label: 'All' },
                    ...uniqueUserAgencies.map(agency => ({
                        value: agency.AgencyID ? agency.AgencyID.toString() : "",
                        label: agency.CompanyName || 'Company'
                    }))
                ];

                return (
                    <div key={fieldID} className="space-y-1.5">
                        <Label htmlFor={fieldID} required={required}>
                            {label}
                        </Label>
                        <Select
                            value={selectedAgencyID}
                            onValueChange={(value) => {
                                setSelectedAgencyID(value);
                                if (value !== 'all' && taskFormData.connID && taskFormData.connID !== '0') {
                                    const connObj = allConnections.find(c => String(c.ConnID) === String(taskFormData.connID));
                                    if (connObj && String(connObj.AgencyID) !== String(value)) {
                                        setTaskFormData(prev => ({
                                            ...prev,
                                            connID: ''
                                        }));
                                    }
                                }
                            }}
                        >
                            <SelectTrigger className="h-11 text-sm sahi-input">
                                <span className={selectedAgencyID ? "text-primary" : "text-muted-foreground"}>
                                    {options.find(opt => opt.value === selectedAgencyID)?.label || placeholder}
                                </span>
                            </SelectTrigger>
                            <SelectContent className="max-h-[200px] bg-primary-foreground border-gray-200 shadow-lg">
                                {options.map((option, index) => (
                                    <SelectItem key={index} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                );
            }

            // Render priority buttons as a single binary checkbox
            else if (type === 'priorityButtons') {
                return null;
            }

            // Render all form fields that use a searchSelect input
            else if (type === 'searchSelect') {
                let searchOptions = [];
                if (fieldID === 'connID') {
                    const nonColdConnections = agencyAndSelectedFilteredConnections.filter(
                        conn => conn.IsCold !== 1 && conn.IsCold !== true
                    );
                    searchOptions = getConnectionOptionsWithSearch(nonColdConnections);
                }
                // Only lock the UI if it was originally assigned a connection
                const wasOriginallyAssigned = prefilledData?.connID && prefilledData?.connID !== '0';
                const isConnectionLocked = fieldID === 'connID' && prefilledData?.connectionLocked && wasOriginallyAssigned;

                if (fieldID === 'connID') {
                    if (!openContact) return null;
                    return (
                        <div key={fieldID} className="space-y-1.5">
                            <Label htmlFor={fieldID} required={required}>
                                {label}
                            </Label>
                            {isConnectionLocked ? (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="h-11 px-3 py-2 border border-gray-200 bg-gray-50 rounded-md flex items-center justify-between text-sm text-gray-600 cursor-not-allowed">
                                                <span>
                                                    {searchOptions.find(opt => String(opt.value) === String(taskFormData[fieldID]))?.label || 'Contact Selected'}
                                                </span>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Contact already selected</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            ) : (
                                <CustomSearchableDropdown
                                    options={searchOptions}
                                    value={taskFormData[fieldID]}
                                    onSelect={(value, option, searchText) => handleSearchSelectChange(fieldID, option, value, searchText)}
                                    placeholder={placeholder}
                                    searchPlaceholder={fieldID === 'connID' ? "Search by name, city, or contact person..." : "Search..."}
                                    displayKey="label"
                                    valueKey="value"
                                    label={label}
                                    allowAdd={allowAdd || false}
                                    addText={addText || ''}
                                    emptyMessage={`No ${label.toLowerCase()} found`}
                                    mobileDropdownText="Select Contact"
                                    enableMultiFieldSearch={fieldID === 'connID'}
                                    renderOption={(option, isSelected) => {
                                        if (fieldID === 'connID') {
                                            return (
                                                <div className="flex flex-col items-start w-full min-w-0">
                                                    <span className="truncate block w-full">
                                                        {option.label}
                                                    </span>
                                                </div>
                                            );
                                        } else {
                                            return (
                                                <span className="text-gray-900">{option.label}</span>
                                            );
                                        }
                                    }}
                                    changeType="TSK"
                                    showAddInHeader={true}
                                />
                            )}
                        </div>
                    );
                }

                return (
                    <div key={fieldID} className="space-y-1.5">
                        <Label htmlFor={fieldID} required={required}>
                            {label}
                        </Label>
                        <CustomSearchableDropdown
                            options={searchOptions}
                            value={taskFormData[fieldID]}
                            onSelect={(value, option, searchText) => handleSearchSelectChange(fieldID, option, value, searchText)}
                            placeholder={placeholder}
                            displayKey="label"
                            valueKey="value"
                            label={label}
                            allowAdd={allowAdd || false}
                            addText={addText || ''}
                            emptyMessage={`No ${label.toLowerCase()} found`}
                        />
                    </div>
                );
            }

            // Render all form fields that use a multiSelect input
            else if (type === 'multiSelect') {
                let options = [];
                if (fieldID === 'threads') {
                    options = filteredThreads;
                }
                else if (fieldID === 'contracts') {
                    options = contracts.map((contract) => ({
                        value: contract.ContractID ? contract.ContractID.toString() : "",
                        label: `#${contract.ContractNo} - ${contract.ContractType} (${contract.Product})`
                    }));

                }

                return (
                    <div key={fieldID} className="space-y-1.5 w-full">
                        <Label htmlFor={fieldID} required={required}>
                            {label}
                        </Label>
                        <MultiSelect
                            variant="default"
                            options={options}
                            onValueChange={(values) => handleFieldChange(fieldID, values)}
                            defaultValue={taskFormData[fieldID]}
                            placeholder={fieldID == "threads" ? 'Select ' + currActiveThreadType.typPlr : 'Select Contracts'}
                            animation={2}
                            maxCount={50}
                            isSelectAll={false}
                            isSearch={false}
                            closeIcon={false}
                            disabled={fieldID == "threads" ? (isThreadSelectDisabled || prefilledData?.threadLocked) :
                                fieldID == "contracts" ? prefilledData?.contractLocked : false
                            } // Disable if no threads or thread is locked
                        />
                    </div>
                );
            }

            else if (type === 'observersMultiSelect') {
                const pocNameCounts = {};
                agencyFilteredUsers.forEach(u => {
                    const n = (u.name || '').trim().toLowerCase();
                    if (n && n !== 'myself') {
                        pocNameCounts[n] = (pocNameCounts[n] || 0) + 1;
                    }
                });

                // For meetings: filter observers to only show users from the POC's agency,
                // but only when the POC is someone other than "myself" (i.e. a specific other user is assigned)
                let usersForObservers = agencyFilteredUsers;
                if (Number(taskFormData.taskClass) === 2 && taskFormData.pocUserID && String(taskFormData.pocUserID) !== '0' && !isSelfUserID(taskFormData.pocUserID)) {
                    const pocUser = users.find(u => String(u.userID) === String(taskFormData.pocUserID));
                    if (pocUser) {
                        const pocAgencyID = Number(pocUser.agencyID || 0);
                        usersForObservers = users.filter(u => Number(u.agencyID || 0) === pocAgencyID);
                    }
                }

                // Exclude logged in user (creator) and assignee (pocUserID)
                const selectableObservers = usersForObservers.filter(user =>
                    !isSelfUserID(user.userID) &&
                    String(user.userID) !== String(taskFormData.pocUserID)
                );

                const observerAgencies = new Set(usersForObservers.map(u => Number(u.agencyID || 0)));
                const isSingleAgency = observerAgencies.size <= 1;

                const options = selectableObservers.map(user => {
                    const trimmed = (user.name || '').trim().toLowerCase();
                    const isDup = pocNameCounts[trimmed] > 1;
                    const fullAgnName = user.agencyName || user.AgencyName || getAgencyNameByID(user.agencyID || user.AgencyID) || '';
                    const agnName = (!isSingleAgency && isDup) ? fullAgnName : '';
                    return {
                        value: user.userID,
                        label: user.name,
                        agencyName: agnName,
                        fullAgencyName: fullAgnName,
                        searchableText: `${user.name} ${fullAgnName}`.toLowerCase()
                    };
                });

                if (options.length === 0) {
                    return null;
                }

                const dynamicLabel = Number(taskFormData.taskClass || 1) === 2 ? 'Attendees' : 'Observers';
                const dynamicPlaceholder = Number(taskFormData.taskClass || 1) === 2 ? 'Select Attendees' : 'Select Observers';

                return (
                    <div key={fieldID} className="space-y-1.5 w-full">
                        <Label htmlFor={fieldID} required={required}>
                            {dynamicLabel}
                        </Label>
                        <CustomSearchableDropdown
                            fieldID={fieldID}
                            options={options}
                            value={taskFormData[fieldID] || []}
                            onSelect={(values) => handleFieldChange(fieldID, values)}
                            placeholder={dynamicPlaceholder}
                            label={dynamicLabel}
                            required={required}
                            isMulti={true}
                            cancellable={false}
                            MultiSelect={true}
                        />
                    </div>
                );
            }

            else if (type === 'multiSelectSecConns') {
                const options = [...secondaryContacts];
                if (!options.length) return

                return (
                    <div key={fieldID} className="space-y-1.5 w-full">
                        <Label htmlFor={fieldID} required={required}>
                            {label}
                        </Label>
                        <MultiSelect
                            variant="default"
                            options={options}
                            onValueChange={(values) => handleFieldChange(fieldID, values)}
                            defaultValue={taskFormData[fieldID]}
                            placeholder={'Select Contacts'}
                            label={'contacts'}
                            outsideLabel={true}
                            selectWidth={isMobile ? '100%' : '50%'}
                            animation={2}
                            maxCount={50}
                            isSelectAll={false}
                            isSearch={true}
                            closeIcon={false}
                            emptyMessage={'No contacts found.'}
                        />
                    </div>
                );
            }

            // Render all form fields that use a date input
            else if (type === 'date') {
                const parseCustomDate = (dateStr) => {
                    if (!dateStr) return null;
                    const parts = dateStr.split('-');
                    if (parts.length === 3) {
                        const day = parseInt(parts[0], 10);
                        const monthStr = parts[1].toLowerCase();
                        let year = parseInt(parts[2], 10);
                        if (year < 100) year += 2000;
                        const monthsMap = {
                            jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
                            jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11
                        };
                        const month = monthsMap[monthStr];
                        if (month !== undefined && !isNaN(day) && !isNaN(year)) {
                            return new Date(year, month, day);
                        }
                    }
                    return new Date(dateStr);
                };

                const now = new Date();
                const currentDueObj = parseCustomDate(taskFormData.dueDT);
                let isDuePast = false;
                let isDueToday = false;
                if (currentDueObj && !isNaN(currentDueObj.getTime())) {
                    const todayMidnight = new Date(now);
                    todayMidnight.setHours(0, 0, 0, 0);

                    const dueMidnight = new Date(currentDueObj);
                    dueMidnight.setHours(0, 0, 0, 0);

                    const combinedDue = taskFormData.dueTime
                        ? new Date(combineDateTime(taskFormData.dueDT, taskFormData.dueTime))
                        : currentDueObj;

                    if (dueMidnight < todayMidnight) {
                        isDuePast = true;
                    } else if (dueMidnight.getTime() === todayMidnight.getTime()) {
                        isDueToday = true;
                    }
                }

                const handleSetToday5PM = (e) => {
                    e?.stopPropagation();
                    e?.preventDefault();

                    const [eodH, eodM] = userEODTime.split(':').map(Number);
                    const eodToday = new Date(now);
                    eodToday.setHours(eodH, eodM, 0, 0);

                    const todayStr = format(now, 'dd-MMM-yy');
                    let finalTime = userEODTime;

                    if (now >= eodToday) {
                        finalTime = getSuggestedTodayTime(now);
                    }

                    let newRemindDate = '';
                    let newRemindTime = '';
                    if (taskFormData.isRemind) {
                        const newDueDT = new Date(`${todayStr} ${finalTime}`);
                        const roundedOneHourBefore = new Date(newDueDT);
                        roundedOneHourBefore.setHours(roundedOneHourBefore.getHours() - 1, 0, 0, 0);
                        if (roundedOneHourBefore > now) {
                            newRemindDate = format(roundedOneHourBefore, 'dd-MMM-yy');
                            newRemindTime = format(roundedOneHourBefore, 'HH:mm');
                        }
                    }

                    const updateRepeatDTForNewDueDate = (baseFormData, newDueDateStr, finalDueTime) => {
                        const hasExistingDbRepeat = Boolean(prefilledData?.repeatDT || (prefilledData?.repeatAfter && Number(prefilledData.repeatAfter) > 0));
                        if (hasExistingDbRepeat) return {};

                        if (baseFormData.repeatDT || (repeatEnabled && (!baseFormData.repeatAfter || Number(baseFormData.repeatAfter) === 0))) {
                            const newDueObj = parseCustomDate(newDueDateStr) || new Date(newDueDateStr);
                            if (newDueObj && !isNaN(newDueObj.getTime())) {
                                const newDay = newDueObj.getDate();
                                let nextYY = newDueObj.getFullYear();
                                let nextMM = newDueObj.getMonth() + 1;
                                if (nextMM > 11) {
                                    nextMM = 0;
                                    nextYY += 1;
                                }
                                const nextMonthLastDay = new Date(nextYY, nextMM + 1, 0).getDate();
                                const clampedDay = Math.min(newDay, nextMonthLastDay);
                                const datePart = format(new Date(nextYY, nextMM, clampedDay), 'dd-MMM-yy');
                                const newRepeatDT = combineDateTime(datePart, finalDueTime || baseFormData.dueTime) || datePart;
                                lastMonthDayRef.current = newDay;
                                return { repeatDT: newRepeatDT, repeatAfter: 0 };
                            }
                        }
                        return {};
                    };

                    setTaskFormData(prev => {
                        const repeatUpdates = updateRepeatDTForNewDueDate(prev, todayStr, finalTime);
                        return {
                            ...prev,
                            dueDT: todayStr,
                            dueTime: finalTime,
                            ...(taskFormData.isRemind && newRemindDate ? { remindDate: newRemindDate, remindTime: newRemindTime } : {}),
                            ...repeatUpdates,
                        };
                    });
                    setIsTaskTimeModified(true);
                    setOpenTaskTime(true);
                    setShowReminderTag(false);
                    setIsReminderFieldsDisabled(false);
                };

                const handleSetTmrwEOD = (e) => {
                    e?.stopPropagation();
                    e?.preventDefault();

                    const tmrw = new Date(now);
                    tmrw.setDate(tmrw.getDate() + 1);
                    const tmrwStr = format(tmrw, 'dd-MMM-yy');
                    const finalTime = userEODTime;

                    let newRemindDate = '';
                    let newRemindTime = '';
                    if (taskFormData.isRemind) {
                        const newDueDT = new Date(`${tmrwStr} ${finalTime}`);
                        const roundedOneHourBefore = new Date(newDueDT);
                        roundedOneHourBefore.setHours(roundedOneHourBefore.getHours() - 1, 0, 0, 0);
                        if (roundedOneHourBefore > now) {
                            newRemindDate = format(roundedOneHourBefore, 'dd-MMM-yy');
                            newRemindTime = format(roundedOneHourBefore, 'HH:mm');
                        }
                    }

                    const updateRepeatDTForNewDueDate = (baseFormData, newDueDateStr, finalDueTime) => {
                        const hasExistingDbRepeat = Boolean(prefilledData?.repeatDT || (prefilledData?.repeatAfter && Number(prefilledData.repeatAfter) > 0));
                        if (hasExistingDbRepeat) return {};

                        // If repeat is in month mode (either repeatDT is set, or repeat is enabled in month mode)
                        if (baseFormData.repeatDT || (repeatEnabled && (!baseFormData.repeatAfter || Number(baseFormData.repeatAfter) === 0))) {
                            const newDueObj = parseCustomDate(newDueDateStr) || new Date(newDueDateStr);
                            if (newDueObj && !isNaN(newDueObj.getTime())) {
                                const newDay = newDueObj.getDate();
                                let nextYY = newDueObj.getFullYear();
                                let nextMM = newDueObj.getMonth() + 1;
                                if (nextMM > 11) {
                                    nextMM = 0;
                                    nextYY += 1;
                                }
                                const nextMonthLastDay = new Date(nextYY, nextMM + 1, 0).getDate();
                                const clampedDay = Math.min(newDay, nextMonthLastDay);
                                const datePart = format(new Date(nextYY, nextMM, clampedDay), 'dd-MMM-yy');
                                const newRepeatDT = combineDateTime(datePart, finalDueTime || baseFormData.dueTime) || datePart;
                                lastMonthDayRef.current = newDay;
                                return { repeatDT: newRepeatDT, repeatAfter: 0 };
                            }
                        }
                        return {};
                    };

                    setTaskFormData(prev => {
                        const repeatUpdates = updateRepeatDTForNewDueDate(prev, tmrwStr, finalTime);
                        return {
                            ...prev,
                            dueDT: tmrwStr,
                            dueTime: finalTime,
                            ...(taskFormData.isRemind && newRemindDate ? { remindDate: newRemindDate, remindTime: newRemindTime } : {}),
                            ...repeatUpdates,
                        };
                    });
                    setIsTaskTimeModified(true);
                    setOpenTaskTime(true);
                    setShowReminderTag(false);
                    setIsReminderFieldsDisabled(false);
                };

                return (
                    <div>
                        {(isDuePast || isDueToday) && (
                            <div className="flex items-center justify-between mb-1.5">
                                <Label htmlFor={fieldID} required={required}>
                                    {label}
                                </Label>
                                {isDuePast ? (
                                    <button
                                        type="button"
                                        onClick={handleSetToday5PM}
                                        className="text-[14px] underline underline-offset-4 leading-none text-muted-foreground hover:underline cursor-pointer font-medium"
                                    >
                                        Today
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleSetTmrwEOD}
                                        className="text-[14px] underline underline-offset-4 leading-none text-muted-foreground hover:underline cursor-pointer font-medium"
                                    >
                                        Tmrw
                                    </button>
                                )}
                            </div>
                        )}
                        <DatePicker
                            fieldID={fieldID}
                            label={(isDuePast || isDueToday) ? undefined : label}
                            placeholder={placeholder}
                            required={true}
                            value={taskFormData[fieldID] ? new Date(taskFormData[fieldID]) : undefined}
                            onChange={(date) => {
                                if (!date) { handleFieldChange(fieldID, ''); return; }
                                const newDateStr = format(date, 'dd-MMM-yy');
                                const now = new Date();

                                const selectedDate = new Date(date);
                                selectedDate.setHours(0, 0, 0, 0);
                                const todayMidnight = new Date(now);
                                todayMidnight.setHours(0, 0, 0, 0);
                                const isToday = selectedDate.getTime() === todayMidnight.getTime();
                                const isFuture = selectedDate.getTime() > todayMidnight.getTime();

                                const [eodH, eodM] = userEODTime.split(':').map(Number);
                                const eodToday = new Date(now);
                                eodToday.setHours(eodH, eodM, 0, 0);

                                let finalTime = taskFormData.dueTime || userEODTime;
                                let timeModified = false;

                                if (isToday) {
                                    const combined = new Date(`${newDateStr} ${finalTime}`);
                                    if (now >= eodToday || combined <= now) {
                                        finalTime = getSuggestedTodayTime(now);
                                        timeModified = true;
                                    }
                                } else if (isFuture) {
                                    finalTime = userEODTime;
                                    timeModified = true;
                                }

                                // Recalculate reminder for any date change using 60/30/15/5/0 min intervals
                                let newRemindDate = '';
                                let newRemindTime = '';
                                if (taskFormData.isRemind) {
                                    const newDueDT = new Date(`${newDateStr} ${finalTime}`);
                                    const roundedReminder = getBeforeDueReminder(newDueDT, now);
                                    if (roundedReminder) {
                                        newRemindDate = format(roundedReminder, 'dd-MMM-yy');
                                        newRemindTime = format(roundedReminder, 'HH:mm');
                                    }
                                }

                                const updateRepeatDTForNewDueDate = (baseFormData, newDueDateStr, finalDueTime) => {
                                    const hasExistingDbRepeat = Boolean(prefilledData?.repeatDT || (prefilledData?.repeatAfter && Number(prefilledData.repeatAfter) > 0));
                                    if (hasExistingDbRepeat) return {};

                                    if (baseFormData.repeatDT || (repeatEnabled && (!baseFormData.repeatAfter || Number(baseFormData.repeatAfter) === 0))) {
                                        const newDueObj = parseCustomDate(newDueDateStr) || new Date(newDueDateStr);
                                        if (newDueObj && !isNaN(newDueObj.getTime())) {
                                            const newDay = newDueObj.getDate();
                                            let nextYY = newDueObj.getFullYear();
                                            let nextMM = newDueObj.getMonth() + 1;
                                            if (nextMM > 11) {
                                                nextMM = 0;
                                                nextYY += 1;
                                            }
                                            const nextMonthLastDay = new Date(nextYY, nextMM + 1, 0).getDate();
                                            const clampedDay = Math.min(newDay, nextMonthLastDay);
                                            const datePart = format(new Date(nextYY, nextMM, clampedDay), 'dd-MMM-yy');
                                            const newRepeatDT = combineDateTime(datePart, finalDueTime || baseFormData.dueTime) || datePart;
                                            lastMonthDayRef.current = newDay;
                                            return { repeatDT: newRepeatDT, repeatAfter: 0 };
                                        }
                                    }
                                    return {};
                                };

                                setTaskFormData(prev => {
                                    const repeatUpdates = updateRepeatDTForNewDueDate(prev, newDateStr, finalTime);
                                    return {
                                        ...prev,
                                        [fieldID]: newDateStr,
                                        dueTime: finalTime,
                                        ...(taskFormData.isRemind && newRemindDate ? { remindDate: newRemindDate, remindTime: newRemindTime } : {}),
                                        ...repeatUpdates,
                                    };
                                });
                                if (timeModified) {
                                    setIsTaskTimeModified(true);
                                    setOpenTaskTime(true);
                                }
                                setShowReminderTag(false);
                                setIsReminderFieldsDisabled(false);
                            }}
                            disabled={{ before: new Date() }}
                            minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                        />
                    </div>
                )
            }

            // Render all form fields that use a time input
            else if (type === 'time') {
                return (
                    <div className="">
                        {openTaskTime || isTaskTimeModified ? (
                            <TimeInputWithDialog
                                key={fieldID}
                                fieldID={fieldID}
                                label={label}
                                value={taskFormData[fieldID]}
                                date={taskFormData.dueDT}
                                onChange={(value) => {
                                    handleFieldChange(fieldID, value);
                                    setIsTaskTimeModified(true);
                                    if (taskFormData.isRemind) {
                                        const now = new Date();
                                        const newDueDT = new Date(combineDateTime(taskFormData.dueDT, value));
                                        const targetRemind = getBeforeDueReminder(newDueDT, now);
                                        if (targetRemind && targetRemind < newDueDT) {
                                            setTaskFormData(prev => ({
                                                ...prev,
                                                dueTime: value,
                                                remindDate: format(targetRemind, 'dd-MMM-yy'),
                                                remindTime: format(targetRemind, 'HH:mm')
                                            }));
                                        }
                                    }
                                }}
                                placeholder={placeholder}
                                canClose={false}
                                onClose={() => {
                                    setOpenTaskTime(false);
                                    setIsTaskTimeModified(false);
                                    setTaskFormData(prev => ({ ...prev, dueTime: userEODTime }));
                                }
                                }
                                required
                            />
                        ) : (
                            <div className="w-full">
                                {label && (
                                    <div className="min-h-[20px] mb-1.5">
                                        <span className="text-sm font-medium text-muted-foreground">

                                        </span>
                                    </div>
                                )}

                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-11 w-full justify-start text-muted-foreground hover:text-primary rounded-md"
                                    onClick={() => {
                                        setOpenTaskTime(true);
                                        setTaskFormData(prev => ({ ...prev, dueTime: userEODTime }));
                                    }}
                                >
                                    + <span className="underline underline-offset-4">Set Time</span>
                                </Button>
                            </div>
                        )}
                    </div>
                );
            }

            // Render all form fields that use a text input
            else if (type === 'text') {
                const isTaskName = fieldID === 'taskName';
                const isHighPriority = taskFormData['priority'] === 'H';
                const currentLength = isTaskName ? (taskFormData[fieldID]?.length || 0) : 0;
                return (
                    <div key={fieldID} className="space-y-1.5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-baseline">
                                <Label htmlFor={fieldID} required={required}>
                                    {label}
                                </Label>
                            </div>
                            {isTaskName && !isNextTaskMode && (
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <input
                                        type="checkbox"
                                        id="priority-inline"
                                        checked={isHighPriority}
                                        onChange={(e) => handleFieldChange('priority', e.target.checked ? 'H' : 'M')}
                                        className="h-4 w-4 rounded border-gray-300 text-sahi-primary-color focus:ring-sahi-primary-color cursor-pointer accent-sahi-primary-color flex-shrink-0"
                                    />
                                    <Label htmlFor="priority-inline" className="cursor-pointer mb-0 whitespace-nowrap">
                                        {/* High Priority */}
                                        Important
                                    </Label>
                                </div>
                            )}
                        </div>
                        <div className="relative">
                            <Input
                                id={fieldID}
                                name={fieldID}
                                value={taskFormData[fieldID]}
                                onChange={(e) => handleFieldChange(fieldID, e.target.value)}
                                placeholder={placeholder}
                                type={type}
                                className={`h-11 text-sm ${isTaskName && isVoiceInputAllowed && !isIOSDevice() ? 'pr-20' : ''}`}
                                maxLength={isTaskName ? 50 : undefined}
                                disabled={isLockedClass && isTaskName}
                            />
                            {isTaskName && isVoiceInputAllowed && !isIOSDevice() && (
                                <SpeechControls
                                    targetId={fieldID}
                                    value={taskFormData[fieldID]}
                                    onChange={(val) => handleFieldChange(fieldID, val)}
                                    isMobile={isMobile}
                                    className="absolute right-2 top-1/2 -translate-y-1/2"
                                />
                            )}
                            {isTaskName && currentLength >= 45 && (
                                <span className="absolute right-0 -bottom-5  text-[11px] text-sub-text-color pointer-events-none select-none">
                                    {currentLength}/50
                                </span>
                            )}
                        </div>
                    </div>
                );
            }

            else if (type === 'classSelect') {
                const isEditMode = Boolean(prefilledData?.taskID);
                return (
                    <div key={fieldID} className="space-y-1.5">
                        <div className="flex flex-wrap gap-2 items-center">
                            {taskClassConfig.taskClasses.map((cls) => {
                                const isSel = Number(taskFormData.taskClass || 1) === cls.id;
                                return (
                                    <button
                                        key={cls.id}
                                        type="button"
                                        disabled={isEditMode}
                                        onClick={() => {
                                            const classVal = cls.id;
                                            handleFieldChange('taskClass', classVal);
                                            if (classVal === 4) {
                                                handleFieldChange('options', ['Approve', 'Disapprove', 'Other']);
                                                const adminID = Number(agencyData?.AdminUserID || 0);
                                                const myUserIDs = getMyAgenciesMappedIDs('UserID');
                                                const isMe = adminID === curLoggedInUser || myUserIDs.includes(adminID);
                                                if (adminID && !isMe) {
                                                    handleFieldChange('pocUserID', adminID);
                                                } else {
                                                    const fallbackUser = users.find(u =>
                                                        Number(u.agencyID) === Number(agencyData?.AgencyID) &&
                                                        u.userID !== curLoggedInUser &&
                                                        !myUserIDs.includes(Number(u.userID))
                                                    );
                                                    if (fallbackUser) {
                                                        handleFieldChange('pocUserID', fallbackUser.userID);
                                                    } else {
                                                        handleFieldChange('pocUserID', '0');
                                                    }
                                                }
                                            } else if (classVal === 3) {
                                                handleFieldChange('options', ['', '']);
                                            } else {
                                                handleFieldChange('options', null);
                                            }
                                            if (classVal !== 1) {
                                                setTaskFormData(prev => ({
                                                    ...prev,
                                                    repeatAfter: 0,
                                                    repeatDT: ''
                                                }));
                                                setRepeatEnabled(false);
                                            }
                                        }}
                                        className={`inline-flex items-center gap-1.5 text-[13px] font-medium px-3 py-1.5 sahi-radius border transition-all ${isSel
                                            ? "bg-sahi-green-extra-light border-sahi-green text-sahi-green font-semibold"
                                            : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                                            } ${isEditMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    >
                                        {isSel && getClassIcon(cls.icon)}
                                        <span>{cls.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            }

            else if (type === 'optionsEditor') {
                const isEditMode = Boolean(prefilledData?.taskID);

                const currentOptions = Array.isArray(taskFormData.options)
                    ? taskFormData.options
                    : (taskFormData.options ? (typeof taskFormData.options === 'string' ? JSON.parse(taskFormData.options) : taskFormData.options) : ['', '']);

                if (taskFormData.taskClass === 4) {
                    return (
                        <div key={fieldID} className="space-y-1.5">
                            <Label>{label}</Label>
                            <div className="bg-zinc-50 border rounded-lg p-3 text-sm text-sub-text-color space-y-1">
                                <div>• Approve</div>
                                <div>• Disapprove</div>
                                <div>• Other (Write free-text details on resolution)</div>
                            </div>
                        </div>
                    );
                }

                return (
                    <div key={fieldID} className="space-y-1.5">
                        <Label>{label}</Label>
                        <div className="space-y-2">
                            {currentOptions.map((opt, optIndex) => (
                                <div key={optIndex} className="flex items-center gap-2">
                                    <Input
                                        value={typeof opt === 'object' && opt !== null ? (opt.OTxt || '') : String(opt)}
                                        onChange={(e) => {
                                            const newOpts = [...currentOptions];
                                            if (typeof opt === 'object' && opt !== null) {
                                                newOpts[optIndex] = { ...opt, OTxt: e.target.value };
                                            } else {
                                                newOpts[optIndex] = e.target.value;
                                            }
                                            handleFieldChange('options', newOpts);
                                        }}
                                        maxLength={85}
                                        disabled={isLockedClass}
                                        placeholder={`Option ${optIndex + 1}`}
                                        className="h-10 text-sm"
                                    />
                                    {!isLockedClass && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                const newOpts = currentOptions.filter((_, idx) => idx !== optIndex);
                                                handleFieldChange('options', newOpts.length === 0 ? null : newOpts);
                                            }}
                                            className="p-1 h-8 w-8 text-sahi-red hover:text-red-800"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                            {!isLockedClass && currentOptions.length < 5 && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        const newOpts = [...currentOptions, ''];
                                        handleFieldChange('options', newOpts);
                                    }}
                                    className="text-muted-foreground px-0 hover:text-primary"
                                >
                                    +<span className="underline underline-offset-4">Add Option</span>
                                </Button>
                            )}
                        </div>
                    </div>
                );
            }

            else if (type === 'shareByEmailCheckbox') {
                // if (taskFormData.pocUserID === curLoggedInUser) {
                //     return null;
                // }

                const isReminderTagShown = showReminderTag && taskFormData.isRemind && taskFormData.remindDate && taskFormData.remindTime && !isReminderFieldsDisabled;

                return (
                    <div key={fieldID} className={`flex items-center gap-2 whitespace-nowrap flex-shrink-0 ${showReminderTag ? 'mt-0.5' : ''} ${isReminderTagShown ? 'w-[calc(50%-0.375rem)] lg:w-auto' : ''}`}>
                        <input
                            type="checkbox"
                            id={fieldID}
                            checked={shareByEmail && isOAuthEnabled}
                            disabled={!isOAuthEnabled}
                            onChange={(e) => handleEmailShareChange(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-sahi-primary-color focus:ring-sahi-primary-color cursor-pointer accent-sahi-primary-color disabled:cursor-not-allowed flex-shrink-0"
                        />
                        <Label htmlFor={fieldID} className={`cursor-pointer mb-0 ${!isOAuthEnabled ? 'text-gray-400 cursor-not-allowed' : ''}`}>
                            {label}
                        </Label>
                        {!isOAuthEnabled && (
                            <TooltipProvider>
                                <Tooltip delayDuration={300}>
                                    <TooltipTrigger asChild>
                                        <button
                                            type="button"
                                            onClick={handleSettingsClick}
                                            className="rounded-full hover:bg-gray-100 transition-colors duration-200 p-0.5"
                                        >
                                            <Settings size={14} className="text-sahi-primary-color hover:text-gray-700" />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" align="center" className="text-xs">
                                        <p>Configure Gmail Integration</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                );
            }

            else if (type === 'shareByWhatsAppCheckbox') {
                return (
                    <div key={fieldID} className="flex items-center gap-2 whitespace-nowrap flex-shrink-0">
                        <input
                            type="checkbox"
                            id={fieldID}
                            checked={shareByWhatsApp}
                            onChange={(e) => setShareByWhatsApp(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-sahi-primary-color focus:ring-sahi-primary-color cursor-pointer accent-sahi-primary-color flex-shrink-0"
                        />
                        <Label htmlFor={fieldID} className="cursor-pointer mb-0">
                            {label}
                        </Label>
                    </div>
                );
            }

            else if (type === 'remindAtCheckbox') {
                const dueDateTime = new Date(combineDateTime(taskFormData.dueDT, taskFormData.dueTime));
                const now = new Date();
                const minutesUntilDue = Math.floor((dueDateTime - now) / 60000);
                const isOverdue = minutesUntilDue < 0;

                if (isOverdue) return null;

                // If task is due in less than 5 minutes, don't show reminder option
                // if (minutesUntilDue < 5 && !taskFormData.isRemind) return null;

                const handleReminderCheck = (checked) => {
                    if (checked && minutesUntilDue < 0) {
                        toast.error("Cannot set a reminder for a task that is already overdue.");
                        return;
                    }

                    setCookie('DefaultTaskRemind', String(Boolean(checked)));

                    if (checked) {
                        const dueDTObj = taskFormData.dueDT && taskFormData.dueTime
                            ? new Date(combineDateTime(taskFormData.dueDT, taskFormData.dueTime))
                            : dueDateTime;

                        const suggested = calculateSuggestedReminder(dueDTObj, userPlanTime, now);
                        const targetRemindDate = suggested?.date ? format(suggested.date, 'dd-MMM-yy') : null;
                        const targetRemindTime = suggested?.time || null;

                        setTaskFormData(prev => ({
                            ...prev,
                            isRemind: true,
                            remindDate: targetRemindDate,
                            remindTime: targetRemindTime
                        }));
                        setShowReminderTag(false); // If checking locally, show inputs, not tag
                    } else {
                        setTaskFormData(prev => ({
                            ...prev,
                            isRemind: false,
                            remindDate: '',
                            remindTime: ''
                        }));
                        setShowReminderTag(false);
                    }
                };

                const isSelf = isSelfUserID(taskFormData.pocUserID);
                const assignedUser = !isSelf && taskFormData.pocUserID
                    ? users.find(u => String(u.userID) === String(taskFormData.pocUserID))
                    : null;
                const assignedName = isSelf ? 'me' : (assignedUser?.name || 'user');
                const remindLabel = taskFormData.isRemind ? `Remind ${assignedName} at` : `Remind ${assignedName}`;

                // Logic for Tag Display (Existing Reminder)
                if (showReminderTag && taskFormData.isRemind && taskFormData.remindDate && taskFormData.remindTime && !isReminderFieldsDisabled) {
                    // Combine date and time for display
                    const rmdrDateTime = combineDateTime(taskFormData.remindDate, taskFormData.remindTime);
                    const rmdrDateObj = new Date(rmdrDateTime);
                    const formattedRmdr = format(rmdrDateObj, 'dd-MMM-yy @ hh:mm aa');

                    return (
                        <React.Fragment key={fieldID}>
                            <div className="w-[calc(50%-0.375rem)] lg:w-auto">
                                <Label htmlFor={fieldID} className="mb-0">{remindLabel}</Label>
                            </div>
                            <div className="order-last lg:order-none w-full lg:w-auto">
                                <div
                                    className="h-11 w-full text-sm sahi-input font-normal rounded-md !gap-0 text-left flex items-center justify-between overflow-hidden p-0"
                                >
                                    <span className="flex-1 min-w-0 px-2 sm:px-3 text-gray-900">
                                        <span className="block truncate">{formattedRmdr}</span>
                                    </span>
                                    <span className="flex-shrink-0 h-full inline-flex items-center justify-center px-2 sm:px-3 bg-white border-l border-gray-300 min-w-[1.5rem] sm:min-w-[1.5rem]">
                                        <Bell className="h-4 w-4 text-gray-700 flex-shrink-0" />
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => handleReminderCheck(false)} // Clicking X removes reminder and shows checkbox
                                        aria-label="Remove reminder"
                                        className="flex-shrink-0 h-full inline-flex items-center justify-center px-2 sm:px-3 bg-white border-l border-gray-300 min-w-[1.5rem] sm:min-w-[1.5rem] cursor-pointer transition-colors hover:text-red-500"
                                    >
                                        <X className="h-4 w-4 flex-shrink-0" />
                                    </button>
                                </div>
                            </div>
                        </React.Fragment>
                    );
                }

                // Logic for Checkbox / Input Display
                return (
                    <div key={fieldID} className="flex items-center gap-2 flex-shrink-0">
                        {/* Checkbox */}
                        <input
                            type="checkbox"
                            id={fieldID}
                            checked={taskFormData.isRemind}
                            onChange={(e) => handleReminderCheck(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-sahi-primary-color focus:ring-sahi-primary-color cursor-pointer accent-sahi-primary-color flex-shrink-0"
                        />
                        <Label htmlFor={fieldID} className="cursor-pointer mb-0 whitespace-nowrap">
                            {remindLabel}
                        </Label>
                    </div>
                );
            }

            else if (type === 'remindAtDateTimePicker') {
                // Render Date and Time Pickers for Reminder (Shown if checked and NOT in Tag View)

                const dueDateTime = new Date(combineDateTime(taskFormData.dueDT, taskFormData.dueTime));
                const isOverdue = dueDateTime < new Date();

                if (isOverdue || !taskFormData.isRemind || (showReminderTag && !isReminderFieldsDisabled)) {
                    return null;
                }

                const handleClearReminder = () => {
                    setTaskFormData(prev => ({ ...prev, isRemind: false, remindDate: '', remindTime: '' }));
                    setShowReminderTag(false);
                };

                return (
                    <div key={fieldID} className="space-y-1.5 w-full">
                        <div className={`mt-2 ${!isMobile ? "w-full" : "grid grid-cols-2 gap-4"}`}>
                            {/* Desktop Layout */}
                            {!isMobile && (
                                <>
                                    {isSmartSellerMode() ? (
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="col-span-1">
                                                <DatePicker
                                                    fieldID="remindDate"
                                                    value={taskFormData.remindDate ? new Date(taskFormData.remindDate) : undefined}
                                                    onChange={(date) => {
                                                        if (!date) return;
                                                        const dateStr = format(date, 'dd-MMM-yy');
                                                        const newRemindDT = new Date(combineDateTime(dateStr, taskFormData.remindTime || '09:00'));
                                                        if (newRemindDT > dueDateTime) {
                                                            toast.error("Reminder must be set upto the due time or earlier.");
                                                            return;
                                                        }
                                                        setTaskFormData(prev => ({ ...prev, remindDate: dateStr }));
                                                    }}
                                                    placeholder="20-Oct-25"
                                                    className="space-y-0"
                                                    disabled={(date) => {
                                                        const dateOnly = new Date(date);
                                                        dateOnly.setHours(0, 0, 0, 0);
                                                        const today = new Date();
                                                        today.setHours(0, 0, 0, 0);
                                                        const dueDate = new Date(taskFormData.dueDT);
                                                        dueDate.setHours(0, 0, 0, 0);

                                                        // Disable dates before today
                                                        if (dateOnly < today) return true;
                                                        // Disable dates after the due date
                                                        if (dateOnly > dueDate) return true;

                                                        return false;
                                                    }}
                                                    minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                                    maxDate={new Date(taskFormData.dueDT)}
                                                    showYearNavigation={true}
                                                />
                                            </div>
                                            <div className="col-span-1">
                                                <TimeInputWithDialog
                                                    fieldID="remindTime"
                                                    value={taskFormData.remindTime}
                                                    onChange={(time) => {
                                                        if (!time) return;
                                                        const dateStr = taskFormData.remindDate || taskFormData.dueDT;
                                                        const newRemindDT = new Date(combineDateTime(dateStr, time));

                                                        if (newRemindDT > dueDateTime) {
                                                            toast.error("Reminder must be set upto the due time or earlier.");
                                                            return;
                                                        }
                                                        if (newRemindDT <= new Date()) {
                                                            toast.error("Reminder must be in the future");
                                                            return;
                                                        }
                                                        setTaskFormData(prev => ({ ...prev, remindTime: time }));
                                                    }}
                                                    placeholder="11:00 AM"
                                                    className="space-y-0"
                                                    hideLabel={true}
                                                    canClose={true}
                                                    onClose={handleClearReminder}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        hasConnectionSelected ?
                                            <div className="grid grid-cols-3 gap-4">
                                                {/* <div className="col-span-2 grid grid-cols-2 gap-4"> */}
                                                <div className="col-span-1">
                                                    <DatePicker
                                                        fieldID="remindDate"
                                                        value={taskFormData.remindDate ? new Date(taskFormData.remindDate) : undefined}
                                                        onChange={(date) => {
                                                            if (!date) return;
                                                            const dateStr = format(date, 'dd-MMM-yy');
                                                            const newRemindDT = new Date(combineDateTime(dateStr, taskFormData.remindTime || '09:00'));
                                                            if (newRemindDT > dueDateTime) {
                                                                toast.error("Reminder must be set upto the due time or earlier.");
                                                                return;
                                                            }
                                                            setTaskFormData(prev => ({ ...prev, remindDate: dateStr }));
                                                        }}
                                                        placeholder="20-Oct-25"
                                                        className="space-y-0"
                                                        disabled={(date) => {
                                                            const dateOnly = new Date(date);
                                                            dateOnly.setHours(0, 0, 0, 0);
                                                            const today = new Date();
                                                            today.setHours(0, 0, 0, 0);
                                                            const dueDate = new Date(taskFormData.dueDT);
                                                            dueDate.setHours(0, 0, 0, 0);

                                                            if (dateOnly < today) return true;
                                                            if (dateOnly > dueDate) return true;
                                                            return false;
                                                        }}
                                                        minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                                        maxDate={new Date(taskFormData.dueDT)}
                                                        showYearNavigation={true}
                                                    />
                                                </div>
                                                <div className="col-span-1">
                                                    <TimeInputWithDialog
                                                        fieldID="remindTime"
                                                        value={taskFormData.remindTime}
                                                        onChange={(time) => {
                                                            if (!time) return;
                                                            const dateStr = taskFormData.remindDate || taskFormData.dueDT;
                                                            const newRemindDT = new Date(combineDateTime(dateStr, time));

                                                            if (newRemindDT > dueDateTime) {
                                                                toast.error("Reminder must be set upto the due time or earlier.");
                                                                return;
                                                            }
                                                            if (newRemindDT <= new Date()) {
                                                                toast.error("Reminder must be in the future");
                                                                return;
                                                            }
                                                            setTaskFormData(prev => ({ ...prev, remindTime: time }));
                                                        }}
                                                        placeholder="11:00 AM"
                                                        className="space-y-0"
                                                        hideLabel={true}
                                                        canClose={true}
                                                        onClose={handleClearReminder}
                                                    />
                                                </div>
                                                {/* </div> */}
                                            </div> :
                                            <div className="grid grid-cols-3 gap-4">
                                                {/* <div className="col-span-2 grid grid-cols-2 gap-4"> */}
                                                <div className="col-span-1">
                                                    <DatePicker
                                                        fieldID="remindDate"
                                                        value={taskFormData.remindDate ? new Date(taskFormData.remindDate) : undefined}
                                                        onChange={(date) => {
                                                            if (!date) return;
                                                            const dateStr = format(date, 'dd-MMM-yy');
                                                            const newRemindDT = new Date(combineDateTime(dateStr, taskFormData.remindTime || '09:00'));
                                                            if (newRemindDT > dueDateTime) {
                                                                toast.error("Reminder must be set upto the due time or earlier.");
                                                                return;
                                                            }
                                                            setTaskFormData(prev => ({ ...prev, remindDate: dateStr }));
                                                        }}
                                                        placeholder="20-Oct-25"
                                                        className="space-y-0"
                                                        disabled={(date) => {
                                                            const dateOnly = new Date(date);
                                                            dateOnly.setHours(0, 0, 0, 0);
                                                            const today = new Date();
                                                            today.setHours(0, 0, 0, 0);
                                                            const dueDate = new Date(taskFormData.dueDT);
                                                            dueDate.setHours(0, 0, 0, 0);

                                                            if (dateOnly < today) return true;
                                                            if (dateOnly > dueDate) return true;
                                                            return false;
                                                        }}
                                                        minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                                        maxDate={new Date(taskFormData.dueDT)}
                                                        showYearNavigation={true}
                                                    />
                                                </div>
                                                <div className="col-span-1">
                                                    <TimeInputWithDialog
                                                        fieldID="remindTime"
                                                        value={taskFormData.remindTime}
                                                        onChange={(time) => {
                                                            if (!time) return;
                                                            const dateStr = taskFormData.remindDate || taskFormData.dueDT;
                                                            const newRemindDT = new Date(combineDateTime(dateStr, time));

                                                            if (newRemindDT > dueDateTime) {
                                                                toast.error("Reminder must be set upto the due time or earlier.");
                                                                return;
                                                            }
                                                            if (newRemindDT <= new Date()) {
                                                                toast.error("Reminder must be in the future");
                                                                return;
                                                            }
                                                            setTaskFormData(prev => ({ ...prev, remindTime: time }));
                                                        }}
                                                        placeholder="11:00 AM"
                                                        className="space-y-0"
                                                        hideLabel={true}
                                                        canClose={true}
                                                        onClose={handleClearReminder}
                                                    />
                                                </div>
                                                {/* </div> */}
                                            </div>
                                    )}
                                </>
                            )}

                            {/* Mobile Layout */}
                            {isMobile && (
                                <>
                                    <div className="flex-1">
                                        <DatePicker
                                            fieldID="remindDate-mobile"
                                            value={taskFormData.remindDate ? new Date(taskFormData.remindDate) : undefined}
                                            onChange={(date) => {
                                                if (!date) return;
                                                const dateStr = format(date, 'dd-MMM-yy');
                                                const newRemindDT = new Date(combineDateTime(dateStr, taskFormData.remindTime || '09:00'));
                                                if (newRemindDT > dueDateTime) {
                                                    toast.error("Reminder cannot be after due time");
                                                    return;
                                                }
                                                setTaskFormData(prev => ({ ...prev, remindDate: dateStr }));
                                            }}
                                            placeholder="20-Oct-25"
                                            className="space-y-0"
                                            disabled={(date) => {
                                                const dateOnly = new Date(date);
                                                dateOnly.setHours(0, 0, 0, 0);
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                const dueDate = new Date(taskFormData.dueDT);
                                                dueDate.setHours(0, 0, 0, 0);

                                                if (dateOnly < today) return true;
                                                if (dateOnly > dueDate) return true;
                                                return false;
                                            }}
                                            minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                            maxDate={new Date(taskFormData.dueDT)}
                                            showYearNavigation={true}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <TimeInputWithDialog
                                            fieldID="remindTime-mobile"
                                            value={taskFormData.remindTime}
                                            onChange={(time) => {
                                                if (!time) return;
                                                const dateStr = taskFormData.remindDate || taskFormData.dueDT;
                                                const newRemindDT = new Date(combineDateTime(dateStr, time));

                                                if (newRemindDT > dueDateTime) {
                                                    toast.error("Reminder cannot be after due time");
                                                    return;
                                                }
                                                if (newRemindDT < new Date()) {
                                                    toast.error("Reminder cannot be in the past");
                                                    return;
                                                }
                                                setTaskFormData(prev => ({ ...prev, remindTime: time }));
                                            }}
                                            placeholder="11:00 AM"
                                            className="space-y-0"
                                            hideLabel={true}
                                            canClose={true}
                                            onClose={handleClearReminder}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                );
            }
            // else if (type == 'dropDown') {
            //     let options = [];
            //     if (fieldID === 'taskType') {
            //         options = taskTypes.map(task => ({
            //             label: task.label,
            //             value: task.value
            //         }));
            //     } else if (fieldID === 'pocUserID') {
            //         options = filteredUsers.map(user => ({ value: user.userID, label: user.name }))
            //     } else if (fieldID === 'priority') {
            //         options = priorities.map(priority => ({
            //             label: priority.label,
            //             value: priority.value
            //         }));
            //     }

            //     return (
            //         <div className='space-y-1.5' key={fieldID}>
            //             <Label htmlFor={fieldID} className={"cursor-pointer mb-1"} required={required} >
            //                 {label}
            //             </Label>
            //             <CustomSearchableDropdown
            //                 fieldID={fieldID}
            //                 required={false}
            //                 onSelect={(value, option) => handleSearchSelectChange(fieldID, option, value)}
            //                 options={options}
            //                 label={label}
            //                 placeholder={placeholder}
            //                 value={taskFormData[fieldID]}
            //                 onChange={(val) => handleFieldChange(fieldID, val)}
            //                 emptyMessage="Please add types in settings."
            //                 searchEnabled={true}
            //                 allowAdd={allowAdd || false}
            //                 addText={addText || ''}
            //             />
            //         </div>
            //     );
            // }
            else if (type == 'dropDown') {
                let options = [];
                if (fieldID === 'taskType') {
                    options = taskTypes.map(task => ({
                        label: task.label,
                        value: task.value
                    }));
                } else if (fieldID === 'pocUserID') {
                    const pocNameCounts = {};
                    agencyFilteredUsers.forEach(u => {
                        const n = (u.name || '').trim().toLowerCase();
                        if (n && n !== 'myself') {
                            pocNameCounts[n] = (pocNameCounts[n] || 0) + 1;
                        }
                    });
                    let filteredUsersForClass = agencyFilteredUsers;
                    if (Number(taskFormData.taskClass) === 4) {
                        const myUserIDs = getMyAgenciesMappedIDs('UserID');
                        filteredUsersForClass = agencyFilteredUsers.filter(user =>
                            user.userID !== curLoggedInUser && !myUserIDs.includes(Number(user.userID))
                        );
                    }
                    options = filteredUsersForClass.map(user => {
                        const trimmed = (user.name || '').trim().toLowerCase();
                        const isDup = pocNameCounts[trimmed] > 1;
                        const fullAgnName = user.agencyName || user.AgencyName || getAgencyNameByID(user.agencyID || user.AgencyID) || '';
                        const agnName = isDup ? fullAgnName : '';
                        return {
                            value: user.userID,
                            label: user.name,
                            agencyName: agnName,
                            fullAgencyName: fullAgnName,
                            searchableText: `${user.name} ${fullAgnName}`.toLowerCase()
                        };
                    });
                } else if (fieldID === 'priority') {
                    options = priorities.map(priority => ({
                        label: priority.label,
                        value: priority.value
                    }));
                }

                // Special handling for taskType field
                if (fieldID === 'taskType') {
                    // const predefinedTypes = recordTypes
                    // 	.filter(rt => ALLOWED_TASK_TYPES.hasOwnProperty(rt.Typ))
                    // 	.map(rt => ({
                    // 		value: rt.Typ,
                    // 		icon: ALLOWED_TASK_TYPES[rt.Typ]
                    // }));

                    const predefinedTypes = Object.keys(ALLOWED_TASK_TYPES)
                        .filter(type =>
                            recordTypes.some(rt => rt.Typ === type)
                        )
                        .map(type => ({
                            value: type,
                            icon: ALLOWED_TASK_TYPES[type]
                        }));

                    const isPredefinedType = (value) => {
                        return predefinedTypes.some(type => type.value === value);
                    };

                    const handleIconClick = (value) => {
                        const option = options.find(opt => opt.value === value);
                        if (option) {
                            handleSearchSelectChange(fieldID, option, value);
                            handleFieldChange(fieldID, value);
                        }
                    };

                    const handleDropdownSelect = (value) => {
                        const option = options.find(opt => opt.value === value);

                        if (option) {
                            handleSearchSelectChange(fieldID, option, value);
                            handleFieldChange(fieldID, value);
                        }

                        // Close dropdown if selected value is one of the predefined types
                        if (isPredefinedType(value)) {
                            setShowDropdown(false);
                        }
                    };

                    const handleMoreClick = () => {
                        setShowDropdown(true);
                    };

                    const handleMoreClickAgain = () => {
                        setShowDropdown(true);
                    };

                    const getIcon = (iconName) => {
                        const icons = {
                            Phone: <Phone className="h-4 w-4" />,
                            MessageCircle: <BsWhatsapp className="h-4 w-4" />,
                            Handshake: <Handshake className="h-4 w-4" />,
                            MapPin: <MapPin className="h-4 w-4" />,
                            PenLine: <PenLine className="h-4 w-4" />,
                            MoreHorizontal: <MoreHorizontal className="h-4 w-4" />
                        };
                        return icons[iconName] || null;
                    };

                    const currentIsPredefined = isPredefinedType(taskFormData[fieldID]);
                    // Always show icons when in external field use (connection is locked), otherwise check hasConnectionSelected
                    const shouldShowIcons = predefinedTypes.length > 0 && (isExtFieldUse || hasConnectionSelected);

                    return (
                        <div className={`space-y-1.5 ${shouldShowIcons && !showDropdown && isMobile ? '' : ''}`} key={fieldID}>
                            <Label htmlFor={fieldID} className="cursor-pointer mb-1" required={required}>
                                {label}
                                {shouldShowIcons && !showDropdown && taskFormData[fieldID] && (
                                    <span className="opacity-60 ml-0.5">
                                        ({taskFormData[fieldID]})
                                    </span>
                                )}
                            </Label>

                            <div className={`grid w-full items-start h-full gap-2 ${shouldShowIcons && !showDropdown ? 'grid-cols-[1fr_auto]' : 'grid-cols-1'}`}>
                                {shouldShowIcons && (!showDropdown) ? (
                                    <>
                                        <div
                                            className="grid gap-2"
                                            style={{ gridTemplateColumns: `repeat(${predefinedTypes.length}, minmax(0, 1fr))` }}
                                        >
                                            {predefinedTypes.map((type) => (
                                                <TooltipProvider key={type.value}>
                                                    <Tooltip delayDuration={300}>
                                                        <TooltipTrigger asChild>
                                                            <div className="flex flex-col items-center gap-1">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleIconClick(type.value)}
                                                                    className={`flex items-center justify-center h-11 w-full rounded-md border transition-colors ${taskFormData[fieldID] === type.value
                                                                        ? 'border-sahi-primary-color bg-white text-sahi-primary-color'
                                                                        : 'border-input bg-white md:hover:border-gray-400'
                                                                        }`}
                                                                >
                                                                    {getIcon(type.icon)}
                                                                </button>
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="top" align="center" className="text-sm">
                                                            <p>{type.value}</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            ))}
                                        </div>
                                        <TooltipProvider>
                                            <Tooltip delayDuration={300}>
                                                <TooltipTrigger asChild>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setShowDropdown(true);
                                                            setAutoOpenDropdown(true);
                                                        }}
                                                        className="flex items-center justify-center h-11 w-11 rounded-md border border-input bg-white md:hover:border-gray-400 transition-colors"
                                                    >
                                                        {getIcon('MoreHorizontal')}
                                                    </button>
                                                </TooltipTrigger>
                                                <TooltipContent side="top" align="center" className="text-sm">
                                                    <p>More Options</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </>
                                ) : (
                                    <CustomSearchableDropdown
                                        fieldID={fieldID}
                                        options={options}
                                        value={taskFormData[fieldID]}
                                        onSelect={(val) => {
                                            handleDropdownSelect(val);
                                            setAutoOpenDropdown(false);
                                        }}
                                        placeholder={placeholder}
                                        cancellable={false}
                                        openDialog={autoOpenDropdown}
                                        onClose={() => {
                                            setShowDropdown(false);
                                            setAutoOpenDropdown(false);
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    );
                }

                const showCanCompleteCheckbox = fieldID === 'pocUserID' && !isSelfUserID(taskFormData.pocUserID);

                return (
                    <div className='space-y-1.5' key={fieldID}>
                        <div className="flex items-center justify-between">
                            <Label htmlFor={fieldID} className={"cursor-pointer"} required={required} >
                                {label}
                            </Label>
                            {showCanCompleteCheckbox && false && (
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <input
                                        type="checkbox"
                                        id="canComplete"
                                        checked={taskFormData.canComplete != false}
                                        onChange={(e) => handleFieldChange('canComplete', e.target.checked)}
                                        className="h-4 w-4 rounded border-gray-300 text-sahi-primary-color focus:ring-sahi-primary-color cursor-pointer accent-sahi-primary-color flex-shrink-0"
                                    />
                                    <Label htmlFor="canComplete" className="cursor-pointer mb-0 whitespace-nowrap">
                                        Can complete
                                    </Label>
                                </div>
                            )}
                        </div>
                        <CustomSearchableDropdown
                            fieldID={fieldID}
                            required={false}
                            onSelect={(value, option) => handleSearchSelectChange(fieldID, option, value)}
                            options={options}
                            label={label}
                            placeholder={placeholder}
                            value={taskFormData[fieldID]}
                            onChange={(val) => handleFieldChange(fieldID, val)}
                            searchEnabled={true}
                            allowAdd={allowAdd || false}
                            addText={addText || ''}
                            emptyMessage={emptyMessage || `No ${label.toLowerCase()} found`}
                            cancellable={false}
                        />
                    </div>
                );
            }

            else if (type === 'myCategory') {
                const activeCategories = myCategories.filter(c => !c.IsD);
                const selectedCatID = taskFormData.myCategory || 0;

                const visibleChips = [];

                const selectedCat = selectedCatID ? activeCategories.find(c => c.CID === selectedCatID) : null;
                if (selectedCat) {
                    visibleChips.push(selectedCat);
                }

                const byUsageDesc = (a, b) => {
                    const diff = (categoryUsageCounts[b.CID] || 0) - (categoryUsageCounts[a.CID] || 0);
                    return diff !== 0 ? diff : a.CNm.localeCompare(b.CNm);
                };

                const defaultCats = activeCategories.filter(c => c.IsDef).sort(byUsageDesc);
                for (const cat of defaultCats) {
                    if (!visibleChips.some(vc => vc.CID === cat.CID)) {
                        visibleChips.push(cat);
                    }
                }

                if (visibleChips.length < 5) {
                    const remainingCats = activeCategories
                        .filter(c => !c.IsDef && c.CID !== selectedCatID)
                        .sort(byUsageDesc);

                    for (const cat of remainingCats) {
                        if (visibleChips.length >= 5) break;
                        if (!visibleChips.some(vc => vc.CID === cat.CID)) {
                            visibleChips.push(cat);
                        }
                    }
                }

                const pinned = selectedCat ? [selectedCat] : [];
                const rest = visibleChips.filter(c => c.CID !== selectedCatID).sort(byUsageDesc);
                visibleChips.length = 0;
                visibleChips.push(...pinned, ...rest);

                const otherCats = activeCategories.filter(c => !visibleChips.some(vc => vc.CID === c.CID));

                return (
                    <div key={fieldID} className="space-y-1.5">
                        <Label>{label}</Label>
                        <div className="flex flex-wrap gap-2 items-center">
                            {visibleChips.map(cat => {
                                const isSel = cat.CID === selectedCatID;
                                return (
                                    <button
                                        key={cat.CID}
                                        type="button"
                                        onClick={() => handleFieldChange('myCategory', isSel ? 0 : cat.CID)}
                                        className="inline-flex items-center gap-1.5 text-[13px] font-medium px-3 py-1.5 sahi-radius border transition-all"
                                        style={isSel
                                            ? { backgroundColor: cat.Clr + '22', borderColor: cat.Clr, color: cat.Clr }
                                            : { backgroundColor: '#fff', borderColor: '#e5e7eb', color: '#374151' }
                                        }
                                    >
                                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cat.Clr }} />
                                        <span className="max-w-[100px] truncate">{cat.CNm}</span>
                                        {/* {isSel && <Check className="h-2.5 w-2.5 shrink-0" strokeWidth={3} />} */}
                                    </button>
                                );
                            })}
                            {otherCats.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => { setCategoryManagerOpen(true); setCategoryManagerInitialView('list'); }}
                                    className="inline-flex items-center text-[13px] font-medium px-3 py-1.5 sahi-radius border border-gray-200 bg-white text-sub-text-color"
                                >
                                    +{otherCats.length} {otherCats.length === 1 ? 'other' : 'others'}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    if (myCategories.filter(c => !c.IsD).length >= 10) {
                                        toast.error('You can only add up to 10 growth areas.');
                                        return;
                                    }
                                    setCategoryManagerInitialView('add'); setCategoryManagerOpen(true);
                                }}
                                className="inline-flex items-center gap-1 text-[13px] font-medium px-3 py-1.5 sahi-radius border border-dashed border-gray-300 bg-white text-sub-text-color hover:border-sahi-primary-color hover:text-sahi-primary-color transition-colors"
                            >
                                <Plus className="h-3 w-3" />
                                New
                            </button>
                        </div>
                    </div>
                );
            }

            else if (type === 'repeatTaskConfig') {
                const isMonthMode = Boolean(taskFormData.repeatDT);
                const activeField = isMonthMode ? 'repeatDT' : 'repeatAfter';
                const otherField = isMonthMode ? 'repeatAfter' : 'repeatDT';

                const parseCustomDate = (dateStr) => {
                    if (!dateStr) return null;
                    const parts = dateStr.split('-');
                    if (parts.length === 3) {
                        const d = parseInt(parts[0], 10);
                        const monthStr = parts[1].toLowerCase();
                        let y = parseInt(parts[2], 10);
                        if (y < 100) y += 2000;
                        const monthsMap = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
                        const m = monthsMap[monthStr];
                        if (m !== undefined && !isNaN(d) && !isNaN(y)) return new Date(y, m, d);
                    }
                    return new Date(dateStr);
                };

                const dueDT = parseCustomDate(taskFormData.dueDT);
                const anchor = (dueDT && !isNaN(dueDT.getTime())) ? dueDT : new Date();
                let nextYY = anchor.getFullYear();
                let nextMM = anchor.getMonth() + 1;
                if (nextMM > 11) {
                    nextMM = 0;
                    nextYY += 1;
                }
                const nextMonthLastDay = new Date(nextYY, nextMM + 1, 0).getDate();
                const maxVal = isMonthMode ? nextMonthLastDay : 366;

                const buildRepeatDTFromDay = (day) => {
                    const datePart = format(new Date(nextYY, nextMM, Math.min(day, nextMonthLastDay)), 'dd-MMM-yy');
                    return combineDateTime(datePart, taskFormData.dueTime) || datePart;
                };

                const getRepeatDayOfMonth = (value) => {
                    if (!value) return null;
                    let dateObj = parse(String(value), 'dd-MMM-yy hh:mm a', new Date());
                    if (isNaN(dateObj.getTime())) dateObj = new Date(value);
                    return isNaN(dateObj.getTime()) ? null : dateObj.getDate();
                };

                // Store / remember prefilled or currently selected values in refs
                const hasExistingDbRepeat = Boolean(prefilledData?.repeatDT || (prefilledData?.repeatAfter && Number(prefilledData.repeatAfter) > 0));

                if (taskFormData.repeatDT) {
                    const day = getRepeatDayOfMonth(taskFormData.repeatDT);
                    if (day && day > 0) lastMonthDayRef.current = day;
                } else if (prefilledData?.repeatDT) {
                    const day = getRepeatDayOfMonth(prefilledData.repeatDT);
                    if (day && day > 0) lastMonthDayRef.current = day;
                }

                if (taskFormData.repeatAfter && Number(taskFormData.repeatAfter) > 0) {
                    lastRepeatAfterRef.current = Number(taskFormData.repeatAfter);
                } else if (prefilledData?.repeatAfter && Number(prefilledData.repeatAfter) > 0) {
                    lastRepeatAfterRef.current = Number(prefilledData.repeatAfter);
                }

                const defaultDayOfMonth = anchor ? anchor.getDate() : 1;

                // When adding a task without existing DB repeat settings, always use the due date's day of month
                const resolvedMonthDay = (!hasExistingDbRepeat)
                    ? (taskFormData.repeatDT ? (getRepeatDayOfMonth(taskFormData.repeatDT) || defaultDayOfMonth) : (lastMonthDayRef.current || defaultDayOfMonth))
                    : (getRepeatDayOfMonth(taskFormData.repeatDT) || lastMonthDayRef.current || defaultDayOfMonth);

                const displayDayValue = isMonthMode
                    ? String(resolvedMonthDay)
                    : (taskFormData.repeatAfter || lastRepeatAfterRef.current || 30);

                const toggleEnabled = (checked) => {
                    setRepeatEnabled(checked);
                    if (checked) {
                        const dayToUse = (!hasExistingDbRepeat)
                            ? defaultDayOfMonth
                            : (getRepeatDayOfMonth(taskFormData.repeatDT) || lastMonthDayRef.current || defaultDayOfMonth);

                        if ((dayToUse) && (!lastRepeatAfterRef.current || taskFormData.repeatDT || !hasExistingDbRepeat)) {
                            lastMonthDayRef.current = dayToUse;
                            handleFieldChange('repeatDT', buildRepeatDTFromDay(dayToUse));
                            handleFieldChange('repeatAfter', 0);
                        } else {
                            const daysToUse = Number(taskFormData.repeatAfter) || lastRepeatAfterRef.current || 30;
                            handleFieldChange('repeatAfter', daysToUse);
                            handleFieldChange('repeatDT', '');
                        }
                    } else {
                        handleFieldChange('repeatAfter', 0);
                        handleFieldChange('repeatDT', '');
                    }
                };

                const toggleMode = () => {
                    const nextIsMonthMode = !isMonthMode;
                    setRepeatDayDraft(null);
                    if (nextIsMonthMode) {
                        const dayToUse = (!hasExistingDbRepeat)
                            ? defaultDayOfMonth
                            : (getRepeatDayOfMonth(taskFormData.repeatDT) || lastMonthDayRef.current || defaultDayOfMonth);
                        lastMonthDayRef.current = dayToUse;
                        handleFieldChange('repeatDT', buildRepeatDTFromDay(dayToUse));
                        handleFieldChange('repeatAfter', 0);
                    } else {
                        const daysToUse = Number(taskFormData.repeatAfter) || lastRepeatAfterRef.current || 30;
                        handleFieldChange('repeatAfter', daysToUse);
                        handleFieldChange('repeatDT', '');
                    }
                };

                const getOrdinalSuffix = (n) => {
                    const num = Number(n);
                    if (!num) return '';
                    const rem100 = num % 100;
                    if (rem100 >= 11 && rem100 <= 13) return 'th';
                    switch (num % 10) {
                        case 1: return 'st';
                        case 2: return 'nd';
                        case 3: return 'rd';
                        default: return 'th';
                    }
                };

                return (
                    <div key={fieldID} className="space-y-1.5">
                        <div className="flex flex-wrap items-center gap-1">
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <input
                                    type="checkbox"
                                    id={fieldID}
                                    checked={repeatEnabled}
                                    onChange={(e) => toggleEnabled(e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-sahi-primary-color focus:ring-sahi-primary-color cursor-pointer accent-sahi-primary-color flex-shrink-0"
                                />
                                <Label htmlFor={fieldID} className="cursor-pointer mb-0 whitespace-nowrap">
                                    {label}
                                </Label>
                            </div>

                            {repeatEnabled && (
                                <div className="flex items-center gap-1">
                                    <span className="text-sm text-text-color font-medium whitespace-nowrap">
                                        {isMonthMode ? 'on date' : 'after'}
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <Input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            value={repeatDayDraft !== null ? repeatDayDraft : displayDayValue}
                                            onFocus={(e) => e.target.select()}
                                            onChange={(e) => {
                                                const digitsOnly = e.target.value.replace(/[^0-9]/g, '');
                                                if (digitsOnly === '') {
                                                    setRepeatDayDraft('');
                                                    if (!isMonthMode) handleFieldChange('repeatAfter', '');
                                                    return;
                                                }
                                                const val = Math.min(Math.max(Number(digitsOnly), 1), maxVal);
                                                setRepeatDayDraft(String(val));
                                                if (isMonthMode) {
                                                    lastMonthDayRef.current = val;
                                                    handleFieldChange('repeatDT', buildRepeatDTFromDay(val));
                                                    handleFieldChange('repeatAfter', 0);
                                                } else {
                                                    lastRepeatAfterRef.current = val;
                                                    handleFieldChange('repeatAfter', val);
                                                    handleFieldChange('repeatDT', '');
                                                }
                                            }}
                                            onBlur={() => setRepeatDayDraft(null)}
                                            onKeyDown={(e) => {
                                                if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
                                                e.preventDefault();
                                                const current = Number(displayDayValue) || 0;
                                                const delta = e.key === 'ArrowUp' ? 1 : -1;
                                                const val = Math.min(Math.max(current + delta, 1), maxVal);
                                                if (isMonthMode) {
                                                    lastMonthDayRef.current = val;
                                                    handleFieldChange('repeatDT', buildRepeatDTFromDay(val));
                                                    handleFieldChange('repeatAfter', 0);
                                                } else {
                                                    lastRepeatAfterRef.current = val;
                                                    handleFieldChange('repeatAfter', val);
                                                    handleFieldChange('repeatDT', '');
                                                }
                                            }}
                                            className={`h-8 w-${isMonthMode ? '10' : '14'} !px-0 text-sm text-center`}
                                        />
                                        {isMonthMode && (
                                            <span className="text-sm text-text-color font-medium whitespace-nowrap">
                                                {getOrdinalSuffix(displayDayValue)}
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-sm text-text-color font-medium whitespace-nowrap">
                                        {isMonthMode ? 'of next month' : (Number(displayDayValue) == 1 ? 'day' : 'days')}
                                    </span>
                                    <TooltipProvider>
                                        <Tooltip delayDuration={300}>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type="button"
                                                    onClick={toggleMode}
                                                    className="flex items-center justify-center h-8 w-8 rounded-full text-muted-foreground hover:text-sahi-primary-color hover:bg-gray-100 transition-colors duration-150 shrink-0"
                                                >
                                                    {isMonthMode ? <RotateCcw className="h-4 w-4" /> : <Calendar className="h-4 w-4" />}
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent side="top" align="center">
                                                <p>{isMonthMode ? 'Switch to repeat after N days' : 'Switch to a fixed day of the month'}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
        };

        // Rendering actions buttons
        const renderActionButtons = () => {
            const currentClassLabel = taskClassConfig.taskClasses.find(c => c.id === Number(taskFormData.taskClass || 1))?.label || 'Task';
            const isEdit = Boolean(prefilledData?.taskID);
            const cls = Number(taskFormData.taskClass || 1);

            let actionText = '';
            if (cls === 2) {
                actionText = isEdit ? 'Update Meeting' : 'Create Meeting Task';
            } else if (cls === 4) {
                actionText = isEdit ? 'Update Approval' : 'Create Approval Task';
            } else if (cls === 3) {
                actionText = isEdit ? 'Update Decision Task' : 'Create Decision Task';
            } else {
                actionText = isEdit ? `Update ${currentClassLabel}` : `Create ${currentClassLabel}`;
            }

            return (
                <div className="py-4 space-y-4 mt-2">
                    <div className="w-full flex flex-col gap-4 sm:flex-row sm:justify-start sm:gap-3">
                        <Button
                            onClick={handleSaveTask}
                            className="w-full sm:w-auto"
                        >
                            <Check className="h-4 w-4" strokeWidth={3} />
                            {actionText}
                        </Button>
                        <Button
                            type="outline"
                            onClick={() => setIsOpen(false)}
                            className="sahi-secondary-button w-full sm:w-auto hidden sm:flex"
                        >
                            <X className="h-4 w-4" strokeWidth={3} />
                            Close
                        </Button>
                    </div>
                </div>
            );
        };


        const isConnectionLocked = React.useMemo(
            () => prefilledData?.connectionLocked === true,
            [prefilledData]
        );
        const connectionName = isConnectionLocked
            ? currentConnection?.BizName ?? null
            : null;
        const computedTitle = React.useMemo(() => {
            const isEdit = Boolean(prefilledData?.taskID);
            const tc = Number(taskFormData.taskClass || 1);
            if (!isEdit) {
                if (tc === 2) return "Add Meeting Task";
                if (tc === 4) return "Add Approval Task";
            } else {
                if (tc === 2) return "Edit Meeting";
                if (tc === 4) return "Edit Approval";
            }
            const action = isEdit ? 'Edit' : 'Add';
            const className = tc === 3 ? 'Decision' : 'Task';
            return `${action} ${className}`;
        }, [prefilledData?.taskID, taskFormData.taskClass]);

        const finalDialogTitle = connectionName
            ? computedTitle
            : computedTitle;

        // Create separate titles for mobile and desktop
        const mobileTitle = isMobile ? getMobileHeaderTitle(computedTitle) : null;
        const desktopTitle = connectionName
            ? `${computedTitle} - ${connectionName}`
            : computedTitle;
        const renderSmartAddBar = () => {
            if (!SHOW_QUICK_TASK_AI || isNextTaskMode) return null;
            return (
                <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-lg space-y-2 mb-2">
                    <div className="flex items-center justify-between">
                        <Label className="text-xs font-semibold text-zinc-600">Quick Add</Label>
                        {smartAddLoading && <span className="text-[10px] text-zinc-400 animate-pulse">Analyzing command...</span>}
                    </div>
                    <div className="relative flex items-center gap-2">
                        <Input
                            id="smart-add-input"
                            placeholder="e.g. Follow up with Harsh tomorrow at 5pm category Sales"
                            value={smartAddText}
                            onChange={(e) => setSmartAddText(e.target.value)}
                            className="h-10 text-xs pr-16 bg-white border-zinc-300"
                        />
                        <SpeechControls
                            targetId="smart-add-input"
                            value={smartAddText}
                            onChange={(val) => setSmartAddText(val)}
                            isMobile={isMobile}
                            className="absolute right-2 top-1/2 -translate-y-1/2"
                            onSpeechComplete={(val) => handleChromeAISmartAdd(val)}
                        />
                    </div>
                    <Button
                        onClick={() => handleChromeAISmartAdd()}
                        disabled={smartAddLoading || !smartAddText.trim()}
                        className="w-full h-8 text-xs sahi-primary-button font-medium"
                    >
                        {smartAddLoading ? "Processing..." : "Add Task"}
                    </Button>
                </div>
            );
        };


        const ClassSelectPill = () => {
            const isEditMode = Boolean(prefilledData?.taskID);
            const activeClass = taskClassConfig.taskClasses.find(c => c.id === Number(taskFormData.taskClass || 1)) || taskClassConfig.taskClasses[0];

            React.useEffect(() => {
                if (!showClassPopover) return;
                const handleGlobalClick = () => setShowClassPopover(false);
                document.addEventListener('click', handleGlobalClick);
                return () => document.removeEventListener('click', handleGlobalClick);
            }, [showClassPopover]);

            return (
                <div className="relative inline-block">
                    <button
                        type="button"
                        disabled={isEditMode}
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowClassPopover(!showClassPopover);
                        }}
                        className="flex items-center gap-2 py-[6px] pl-[6px] pr-[8px] rounded-md cursor-pointer transition-all duration-200 border select-none focus:outline-none disabled:opacity-75 disabled:cursor-not-allowed"
                        style={{
                            backgroundColor: activeClass.bgColor,
                            borderColor: activeClass.color,
                        }}
                    >
                        <span
                            className="w-[18px] h-[18px] rounded-[3px] flex items-center justify-center text-white shrink-0"
                            style={{ backgroundColor: activeClass.color }}
                        >
                            {getClassIcon(activeClass.icon, "h-[11px] w-[11px]")}
                        </span>
                        <span className="text-[13px] font-semibold leading-none" style={{ color: activeClass.color }}>
                            {activeClass.label}
                        </span>
                        <span
                            className="shrink-0 transition-transform duration-200 opacity-70"
                            style={{
                                color: activeClass.color,
                                transform: showClassPopover ? 'rotate(180deg)' : 'rotate(0deg)'
                            }}
                        >
                            <ChevronDown className="h-[13px] w-[13px]" />
                        </span>
                    </button>

                    {showClassPopover && (
                        <div className="absolute top-[calc(100%+8px)] right-0 w-[270px] bg-white border border-zinc-200 rounded-[10px] shadow-[0_16px_40px_-12px_rgba(20,20,20,0.28)] p-1.5 z-30 animate-in fade-in slide-in-from-top-2 duration-150">
                            {taskClassConfig.taskClasses.map((cls) => {
                                const isSel = Number(taskFormData.taskClass || 1) === cls.id;
                                return (
                                    <div
                                        key={cls.id}
                                        onClick={() => {
                                            const classVal = cls.id;
                                            handleFieldChange('taskClass', classVal);
                                            if (classVal === 4) {
                                                handleFieldChange('options', ['Approve', 'Disapprove', 'Other']);
                                            } else if (classVal === 3) {
                                                handleFieldChange('options', ['', '']);
                                            } else {
                                                handleFieldChange('options', null);
                                            }
                                            if (classVal !== 1) {
                                                setTaskFormData(prev => ({
                                                    ...prev,
                                                    repeatAfter: 0,
                                                    repeatDT: ''
                                                }));
                                                setRepeatEnabled(false);
                                            }
                                            setShowClassPopover(false);
                                        }}
                                        className={`flex items-start gap-2.5 p-2 rounded-[10px] cursor-pointer hover:bg-zinc-50 transition-colors ${isSel ? 'bg-zinc-50' : ''
                                            }`}
                                    >
                                        <span
                                            className="w-[26px] h-[26px] rounded-[5px] flex items-center justify-center text-white shrink-0 mt-0.5"
                                            style={{ backgroundColor: cls.color }}
                                        >
                                            {getClassIcon(cls.icon, "h-[14px] w-[14px]")}
                                        </span>
                                        <span className="flex-1 min-w-0 text-left">
                                            <div className="text-[13.5px] font-semibold text-text-color leading-snug text-left w-full">{cls.label}</div>
                                            <div className="text-[11.5px] text-sub-text-color font-normal leading-normal mt-0.5 text-left w-full">{cls.description}</div>
                                        </span>
                                        {isSel && (
                                            <span className="text-text-color shrink-0 mt-1">
                                                <Check className="h-[13px] w-[13px]" strokeWidth={3} />
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        };

        // Rendering task entry component
        return (
            <>
                {triggerButton && (
                    <Button
                        onClick={() => setIsOpen(true)}
                    >
                        <Plus className="h-4 w-4" />
                        {triggerText}
                    </Button>
                )}

                {!isExtFieldUse && isOpen && <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
                    <DialogContent
                        onInteractOutside={(e) => e.preventDefault()}
                        onOpenAutoFocus={(e) => e.preventDefault()}
                        style={{ maxWidth: getDialogMaxWidth() }}
                        className="max-w-[100vw]"
                    >
                        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
                            <div className="sm:hidden flex items-center justify-between w-full h-8">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                        <ArrowLeft className="h-6 w-6" />
                                        <span className="sr-only">Back</span>
                                    </DialogClose>
                                    <DialogTitle className="text-lg font-medium">
                                        <div className="truncate">
                                            {mobileTitle}
                                        </div>
                                    </DialogTitle>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    {!prefilledData?.taskID && !isNextTaskMode && <ClassSelectPill />}
                                </div>
                            </div>
                            <div className="hidden sm:flex items-center justify-between w-full">
                                <DialogTitle className="text-lg md:text-xl flex items-center gap-3">
                                    <span>{desktopTitle}</span>
                                </DialogTitle>
                                <div className="flex items-center gap-3">
                                    {!prefilledData?.taskID && !isNextTaskMode && <ClassSelectPill />}
                                    <DialogClose className="flex-shrink-0" tabIndex={-1}>
                                        <X className="h-5 w-5" />
                                        <span className="sr-only">Close</span>
                                    </DialogClose>
                                </div>
                            </div>
                        </DialogHeader>

                        <div className="px-4 flex-1 overflow-y-auto max-h-[calc(100vh-100px)] sm:max-h-[75vh]">
                            <div className="space-y-4">
                                {renderSmartAddBar()}
                                {renderDisableConn()}
                                {renderDynamicFields()}
                            </div>
                            {renderActionButtons()}
                        </div>
                    </DialogContent>
                </Dialog>
                }

                {isExtFieldUse && isOpen && <div className={`${!extFieldUse && 'px-4'} flex-1 overflow-y-auto max-h-[calc(100vh-100px)] sm:max-h-[75vh]`}>
                    <div className="space-y-4">
                        {!prefilledData?.taskID && !isNextTaskMode && (
                            <div className="flex items-center justify-between pb-1.5 pt-1 border-b border-gray-100 mb-2">
                                <span className="text-sm font-semibold text-zinc-700">Class</span>
                                <ClassSelectPill />
                            </div>
                        )}
                        {renderDisableConn()}
                        {renderDynamicFields()}
                    </div>
                </div>
                }

                {/* Connection entry dialog box */}
                <ConnectionEntryDialog
                    triggerButton={false}
                    open={connectionDialogOpen}
                    onOpenChange={(open) => {
                        setConnectionDialogOpen(open);
                        if (!open) {
                            // Reopen taskEntry dialog when connection dialog closes
                            setIsOpen(true);
                            setConnectionSearchText('');
                        }
                    }}
                    parentDialogSetter={setIsOpen}
                    prefilledData={connectionSearchText ? { businessName: connectionSearchText } : null}
                    onConnectionAdded={async (savedConnection) => {
                        await loadAllConns();
                        if (savedConnection) {
                            const connID = savedConnection.ConnID || savedConnection.connID;

                            // Set the connection ID in form data
                            setTaskFormData(prev => ({
                                ...prev,
                                connID: connID
                            }));

                            // Force re-render to ensure dropdown shows the new selection
                            setTimeout(() => {
                                setTaskFormData(prev => ({
                                    ...prev,
                                    connID: connID
                                }));
                            }, 100);
                        }

                        // Close connection dialog and reopen task dialog
                        setConnectionDialogOpen(false);
                        setIsOpen(true);
                    }}
                    calledFrom="TSK"
                    isBasic={true}
                />

                {/* User entry dialog box - "Add User" from the Assign to dropdown */}
                {userEntryDialogOpen && (
                    <UserEntry
                        isOpen={userEntryDialogOpen}
                        setIsOpen={(open) => {
                            setUserEntryDialogOpen(open);
                            if (!open) {
                                // Reopen taskEntry dialog when user dialog closes
                                setIsOpen(true);
                            }
                        }}
                        userData={null}
                    />
                )}

                {/* Task type entry dialog box */}
                {taskTypeEntryOpen && (
                    <MastersDialog
                        open={taskTypeEntryOpen}
                        onClose={() => {
                            setTaskTypeEntryOpen(false);
                        }}
                        masterType="taskTypes"
                        directAdd={true}
                        parentDialogOpen={isOpen}
                        onProductAdded={handleTaskTypeAdded}
                    />
                )}

                {/* Email Configuration Dialog */}
                <Policies
                    open={emailConfigOpen}
                    onOpenChange={setEmailConfigOpen}
                    configType="EMAIL"
                />

                {/* Task Category Manager */}
                <TaskCategoryManager
                    open={categoryManagerOpen}
                    onOpenChange={(val) => { setCategoryManagerOpen(val); if (!val) setCategoryManagerInitialView('list'); }}
                    categories={myCategories}
                    initialView={categoryManagerInitialView}
                    selectedCID={taskFormData.myCategory || 0}
                    onSelect={(cid) => { handleFieldChange('myCategory', cid); setCategoryManagerOpen(false); setCategoryManagerInitialView('list'); }}
                    onCategoriesChange={(updated) => {
                        setMyCategories(updated);
                        if (!updated.filter(c => !c.IsD).find(c => c.CID === taskFormData.myCategory)) {
                            setTaskFormData(prev => ({ ...prev, myCategory: 0 }));
                        }
                    }}
                />
            </>
        );
    });

export default TaskEntry;